<template>
  <div class="gap-up-analysis" :class="{ 'is-embed-results': embedResults }" v-if="showContent">
    <!-- 查询和添加区域 -->
    <div v-if="!embedResults && !lccBoard" class="search-area">
        <div class="search-head">
            <div class="toolbar-line toolbar-line--date">
              <span class="search-area-label">日期:</span>
              <el-date-picker
                class="search-area-select search-area-select--date"
                :size="isMini"
                v-model="formData.date"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="选择日期"
                @change="changeDate"
              />
            </div>
            <div class="leader-stack">
            <div class="leader-col__row">
              <span class="search-area-label">龙头:</span>
              <el-select
                :size="isMini"
                class="search-area-select search-area-select--leader"
                v-model="selectedStocks"
                filterable
                remote
                multiple
                placeholder="请输入股票名称"
                :remote-method="searchStock"
                :loading="searchLoading"
                @change="handleStockSelect"
              >
                <el-option
                  v-for="item in stockOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item"
                />
              </el-select>
            </div>
            <div class="leader-col__row">
              <span class="search-area-label">TOP:</span>
              <el-select
                :size="isMini"
                class="search-area-select search-area-select--leader"
                v-model="selectedTopStocks"
                multiple
                @change="handleStockSelect"
              >
                <el-option
                  v-for="item in topStockOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item"
                />
              </el-select>
            </div>
            <div class="leader-col__row leader-col__row--node">
            <el-popover
              placement="bottom-start"
              width="480"
              trigger="click"
              popper-class="gap-up-node-popover"
            >
              <div class="greedy-node-hits-panel">
                <div class="greedy-node-hits-panel__head">{{ greedyNodeSchemeTitle }}</div>
                <div v-if="!dayNodeDailyDoc" class="greedy-node-hits-panel__empty">
                  当日节点分待更新，请先「获取数据」或切换有效交易日
                </div>
                <template v-else>
                  <div v-if="dayNodeScore != null" class="greedy-node-hits-panel__score">
                    总分 <b>{{ dayNodeScore }}</b>
                    <span v-if="dayNodeHit" class="greedy-node-hits-panel__badge">达标</span>
                    <span v-else class="greedy-node-hits-panel__badge greedy-node-hits-panel__badge--muted">未达标</span>
                  </div>
                  <div
                    class="greedy-node-hits-panel__complete"
                    :class="{ 'is-ok': dayNodeSnapshotComplete, 'is-warn': !dayNodeSnapshotComplete }"
                  >
                    日因子 {{ dayNodeSnapshotFilled }}/{{ dayNodeSnapshotTotal }}
                    <template v-if="dayNodeSnapshotComplete"> · 已齐</template>
                    <template v-else> · 缺 {{ dayNodeSnapshotMissing.length }} 项</template>
                    <span v-if="dayNodeFactorSource" class="greedy-node-hits-panel__source">{{ dayNodeFactorSourceLabel }}</span>
                  </div>
                  <div class="greedy-node-hits-panel__snapshot">
                    <div
                      v-for="row in dayNodeSnapshotRows"
                      :key="row.key"
                      class="greedy-node-hits-panel__snap-item"
                      :class="{ 'is-missing': row.missing }"
                    >
                      <span class="greedy-node-hits-panel__snap-label">{{ row.label }}</span>
                      <span class="greedy-node-hits-panel__snap-value">{{ row.display }}</span>
                    </div>
                  </div>
                  <div class="greedy-node-hits-panel__section-title">命中节点</div>
                  <div v-if="!dayNodeHits.length" class="greedy-node-hits-panel__empty">无命中节点</div>
                  <div
                    v-for="nodeId in dayNodeHits"
                    :key="nodeId"
                    class="greedy-node-hits-panel__item"
                  >
                    <div class="greedy-node-hits-panel__title">
                      <strong>{{ nodeId }}</strong>
                      <span class="greedy-node-hits-panel__weight">(+{{ getGreedyNodeWeight(nodeId) }})</span>
                      <span class="greedy-node-hits-panel__factor">{{ getGreedyNodeFactor(nodeId) }}</span>
                    </div>
                    <div class="greedy-node-hits-panel__rule">{{ getGreedyNodeRule(nodeId) }}</div>
                  </div>
                </template>
              </div>
              <div
                slot="reference"
                class="day-node-panel"
                :class="{
                  'day-node-panel--hit': dayNodeHit,
                  'day-node-panel--pending': !dayNodeDailyDoc
                }"
              >
                <span class="day-node-panel__label">节点分</span>
                <span class="day-node-panel__scheme">V5F</span>
                <template v-if="dayNodeDailyDoc">
                  <span class="day-node-panel__score">{{ dayNodeScore != null ? dayNodeScore : '—' }}</span>
                  <span v-if="dayNodeHit" class="day-node-panel__mark" title="V5F ≥7">⬡</span>
                  <span v-if="dayNodeHit" class="day-node-panel__status">达标</span>
                </template>
                <span v-else class="day-node-panel__pending">待更新</span>
                <i class="el-icon-arrow-down day-node-panel__arrow" />
              </div>
            </el-popover>
            </div>
            </div>
        </div>
        <div class="toolbar-line toolbar-line--actions">
          <el-button
            :size="isMini"
            class="toolbar-btn"
            :loading="nextDayLoading"
            :disabled="!formData.date"
            @click="handleNextTradeDay"
          >下一日</el-button>
          <el-button
            :size="isMini"
            class="toolbar-btn"
            type="primary"
            @click="analyzeGapUp"
            :disabled="allConceptsList.length === 0"
          >开始分析</el-button>
          <el-button
            :size="isMini"
            class="toolbar-btn"
            type="primary"
            :loading="syncingDayData"
            @click="handleSyncDayData"
          >获取数据</el-button>
        </div>
    </div>

    <!-- 概念展示区域 -->
    <div v-if="!embedResults && !lccBoard" class="concepts-area" :class="{ 'is-collapsed': conceptsCollapsed }">
      <div class="concepts-title" @click="toggleConceptsCollapsed">
        <button type="button" class="concepts-toggle" :aria-expanded="!conceptsCollapsed">
          <i :class="conceptsCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-down'" />
        </button>
        <span class="concepts-title__text">
          概念列表（{{ allConceptsList.length }}）
        </span>
        <div class="add-concept" v-show="!conceptsCollapsed" @click.stop>
          <el-select
            class="concepts-title-input"
            :size="isMini"
            v-model="newConcept"
            filterable
            remote
            clearable
            placeholder="搜索概念池"
            :remote-method="searchConcept"
            :loading="conceptSearchLoading"
          >
            <el-option
              v-for="item in conceptOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-button type="primary" :size="isMini" @click="addCustomConcept" :disabled="!newConcept.trim()">
            添加
          </el-button>
        </div>
      </div>
      <div v-show="!conceptsCollapsed" class="concepts-list">
      <div 
          v-for="(conceptData, index) in allConceptsList" 
          :key="index"
          class="concept-item"
        >
          <el-badge 
            v-if="conceptData.count > 1" 
            :value="conceptData.count" 
            :max="99"
            type="primary"
          >
            <el-tag
              :size="isMini"
              :type="highlightedConcepts.includes(conceptData.name) ? 'danger' : ''"
              @click="toggleHighlight(conceptData.name)"
              class="concept-tag"
            >
              {{ conceptData.name }}
            </el-tag>
          </el-badge>
          <el-tag
            v-else-if="!conceptData.isCustom"
            :size="isMini"
            :type="highlightedConcepts.includes(conceptData.name) ? 'danger' : ''"
            @click="toggleHighlight(conceptData.name)"
            class="concept-tag"
          >
            {{ conceptData.name }}
          </el-tag>
          <el-badge 
            v-else
            value="A" 
            type="danger"
          >
            <el-tag
              :type="highlightedConcepts.includes(conceptData.name) ? 'danger' : ''"
              @click="toggleHighlight(conceptData.name)"
              class="concept-tag"
            >
              {{ conceptData.name }}
            </el-tag>
          </el-badge>
        </div>
      </div>
    </div>
    <!-- 结果表格 -->
    <div class="results-area">
      <div v-if="embedResults" class="embed-leader-bar">
        <span class="search-area-label">龙头:</span>
        <button
          v-for="card in embedDragonCards"
          :key="card.key"
          type="button"
          class="dragon-card"
          :class="['tone-' + card.role, { on: isEmbedDragonOn(card) }]"
          @click="$emit('embed-dragon-toggle', card)"
        >
          <em>{{ card.tag }}</em>
          <b>{{ card.name }}</b>
        </button>
        <el-button size="mini" type="primary" @click="analyzeSelectedDragons">开始分析</el-button>
      </div>
      <div class="results-area-filter">
        <el-button size="mini" @click="openFilterConfig">过滤配置</el-button>
        <el-checkbox v-model="onlyMainBoard" @change="onOnlyMainBoardChange">只看主板</el-checkbox>
        <el-checkbox v-model="onlyChiNext" @change="onOnlyChiNextChange">只看创业板</el-checkbox>
        <el-checkbox v-model="filterMarketValue" @click="checkboxChange">过滤自由流通市值({{ marketCapMaxDisplay }}亿)</el-checkbox>
        <el-checkbox v-model="filterFirstBoardPrice" @click="checkboxChange">过滤首板价({{ firstBoardPriceMaxDisplay }}元)</el-checkbox>
        <el-checkbox v-model="filterMa250">过滤MA250</el-checkbox>
        <el-checkbox v-model="filterSlope">分段斜率剔除 &gt; {{ slopeMinDisplay }}%</el-checkbox>
        <el-checkbox v-if="lccBoard" v-model="filterExcludeRelayTop" @change="loadRelayExclude">剔除今日补涨接力TOP</el-checkbox>
        <el-button
          size="mini"
          :type="viewBaseFilteredOut ? 'warning' : 'default'"
          :plain="!viewBaseFilteredOut"
          @click="viewBaseFilteredOut = !viewBaseFilteredOut"
        >查看被过滤{{ baseFilteredOutCount ? `(${baseFilteredOutCount})` : '' }}</el-button>
        <el-checkbox v-model="filterDistToYearHigh">距年高 ≤ {{ distToYearHighMaxDisplay }}%</el-checkbox>
        <el-checkbox v-model="filterPreScreenScore">综合分 ≥ {{ preScreenScoreMinDisplay }}</el-checkbox>
        <div v-if="lccBoard" class="lcc-sync-stack">
          <el-button
            size="mini"
            type="primary"
            class="sync-ths-btn"
            :loading="syncAllDragonsLoading"
            :disabled="syncThsLoading"
            title="写入同花顺分组「所有龙」：各王国总龙、趋势龙、连板龙，以及今日≥4板"
            @click="syncAllDragonsToThs"
          >同步所有龙头</el-button>
          <el-button
            size="mini"
            type="primary"
            class="sync-ths-btn"
            :loading="syncThsLoading"
            :disabled="syncAllDragonsLoading"
            title="把当前列表写入同花顺分组「切换+月日」，再次点击会删掉同名分组再重建"
            @click="syncGapBoardToThs"
          >同步同花顺</el-button>
        </div>
        <button
          v-if="embedResults"
          type="button"
          class="relay-add-btn"
          :class="{ on: embedPickActive }"
          @click="$emit('relay-add')"
        >{{ embedPickActive ? '完成点选' : '添加' }}</button>
        <div class="chip-tier-filter-row">
          <span class="filter-rel-vol-label">筹码梯队</span>
          <div class="chip-tier-buttons">
            <button
              v-for="opt in chipReviewTierButtons"
              :key="'cr-' + opt.key"
              type="button"
              class="chip-tier-btn"
              :class="{
                'is-active': isChipReviewTierActive(opt.key),
                'is-splus': opt.key === 'S+'
              }"
              @click="toggleChipReviewTier(opt.key)"
            >{{ opt.label }} {{ chipReviewTierCountLabel(opt.key) }}</button>
          </div>
          <span class="filter-current-count">当前：{{ showTableList.length }}只</span>
        </div>
        <div class="chip-tier-filter-row">
          <span class="filter-rel-vol-label">中期筹码</span>
          <div class="chip-tier-buttons">
            <button
              v-for="opt in midChipTierButtons"
              :key="'mid-' + opt.key"
              type="button"
              class="chip-tier-btn"
              :class="{ 'is-active': filterMidChipTiers.includes(opt.key) }"
              @click="toggleChipTier('filterMidChipTiers', opt.key)"
            >{{ opt.label }}</button>
          </div>
        </div>
        <div class="chip-tier-filter-row">
          <span class="filter-rel-vol-label">梯队</span>
          <div class="chip-tier-buttons">
            <button
              v-for="opt in boardTierButtons"
              :key="'board-' + opt.key"
              type="button"
              class="chip-tier-btn"
              :class="{ 'is-active': filterBoardTiers.includes(opt.key) }"
              @click="toggleChipTier('filterBoardTiers', opt.key)"
            >{{ opt.label }}</button>
          </div>
        </div>
      </div>
      <div class="table-wrap">
      <el-table
        ref="limitUpTable"
        :data="showTableList"
        style="width: 100%"
        height="100%"
        :fit="true"
        :row-class-name="embedRowClassName"
        @row-click="onEmbedTableRowClick"
        :cell-class-name="tableCellClassName"
        @sort-change="sortChange"
        border
      >
        <el-table-column
          v-if="embedResults && embedPickActive"
          width="36"
          align="center"
          label=""
        >
          <template slot-scope="{ row }">
            <el-checkbox
              :value="isEmbedPicked(row)"
              @change="toggleEmbedPick(row)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="name" label="股票名称" :width="colWidth('128px', '108px', '72px')" :fixed="embedResults ? false : 'left'">
          <template slot-scope="{ row }">
            <span class="gap-up-name-cell">
              <span v-html="row.nameDisplay || row.name"></span>
              <span
                v-if="topFlagsTextForRow(row)"
                class="gap-up-top-flags"
                title="当日复盘TOP库"
              >{{ topFlagsTextForRow(row) }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="逻辑"
          class-name="gap-up-logic-col"
          :width="colWidth('128px', '110px', '96px')"
        >
          <template slot-scope="{ row }">
            <div class="logic-cell">
              <template v-if="!hasLogicMatch(row)">
                <el-button
                  size="mini"
                  type="primary"
                  @click="goToConceptMatch(row)"
                >匹配</el-button>
                <el-button
                  type="text"
                  size="mini"
                  class="logic-cell__switch"
                  @click="setLogicSwitch(row)"
                >切换</el-button>
              </template>
              <template v-else>
                <span
                  class="logic-cell__dot"
                  title="重新匹配"
                  @click="goToConceptMatch(row)"
                ></span>
                <span class="logic-cell__text">{{ formatLogicDisplay(row) }}</span>
              </template>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="midChip"
          :sortable="'custom'"
          label="中期筹码分"
          :width="colWidth('100px', '96px', '84px')"
          :formatter="formatChipPct"
        />
        <el-table-column
          prop="chipReviewScore"
          :sortable="'custom'"
          label="筹码排序"
          :width="colWidth('108px', '100px', '74px')"
        >
          <template slot-scope="{ row }">
            <div
              v-if="row.chipReviewScore != null"
              class="chip-review-cell"
              :class="chipReviewCellClass(row)"
            >
              <template v-if="row.chipReviewTier === 'S+'">
                <span class="chip-review-cell__mark">✦</span>
                <span class="chip-review-cell__score">{{ row.chipReviewScore }}</span>
                <span class="chip-review-cell__badge">S+</span>
                <span class="chip-review-cell__hint">核心筹码</span>
              </template>
              <template v-else>
                <span class="chip-review-cell__score">{{ row.chipReviewScore }}</span>
                <span class="chip-review-cell__tier">{{ row.chipReviewTier }}</span>
              </template>
            </div>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column
          v-if="!embedResults"
          prop="chipV2Score"
          :sortable="'custom'"
          label="筹码V2"
          :width="colWidth('100px', '90px', '100px')"
        >
          <template slot-scope="{ row }">
            <span>{{ fmtChipV2Score(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          v-if="!embedResults"
          prop="rangeAllD0"
          :sortable="'custom'"
          label="全周期粘合"
          :width="colWidth('110px', '100px', '110px')"
        >
          <template slot-scope="{ row }">
            <span>{{ fmtRangeAllD0(null, null, row.rangeAllD0) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="priceToMax"
          :sortable="'custom'"
          label="首板价格位置"
          :width="colWidth('170px', '150px', '100px')"
        >
          <template slot-scope="{ row }">
            <span :class="{ 'chip-v2-overextended': row.pricePositionLevel === 'OVEREXTENDED' }">
              {{ fmtPriceToMaxWithLabel(row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="preScreenScore"
          :sortable="'custom'"
          label="综合分"
          :width="colWidth('100px', '90px', '60px')"
          :formatter="fmtPreScreenScore"
        />
        <el-table-column
          v-if="!embedResults"
          prop="netInflowToAmount0"
          :sortable="'custom'"
          label="净流占比"
          :width="colWidth('100px', '96px', '100px')"
        >
          <template slot-scope="{ row }">
            <span>{{ fmtRatioPct(null, null, row.netInflowToAmount0) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="distToYearHighPct"
          :sortable="'custom'"
          label="距年高涨幅"
          :width="colWidth('120px', '110px', '84px')"
        >
          <template slot-scope="{ row }">
            <span>{{ fmtDistYearHigh(null, null, row.distToYearHighPct) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="marketValue"
          :sortable="'custom'"
          label="自流通值"
          :width="colWidth('96px', '88px', '72px')"
        >
          <template slot-scope="{ row }">
            <span>{{ fmtMarketValueInt(null, null, row.marketValue) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="limitUpTime" :sortable="'custom'" label="涨停时间" :width="colWidth('130px', '120px', '72px')" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span>{{ formatLimitTime(row.limitUpTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="consecutiveBoards" label="梯队" :sortable="'custom'" :width="colWidth('128px', '116px', '96px')">
          <template slot-scope="{ row }">
            <span
              v-if="row.nodeHit"
              class="node-star"
              :style="nodeStarStyle(row.nodeScore)"
              :title="nodeStarTitle(row)"
            >★</span>
            <span :style="row.nodeHit ? nodeStarStyle(row.nodeScore) : null">{{ formatConsecutiveBoards(row) }}</span>
            <span
              v-if="soilBoardMark(row)"
              class="soil-board-mark"
              :class="daySoil === 'S_PLUS' ? 'is-splus' : 'is-s'"
            >{{ soilBoardMark(row) }}</span>
            <el-tag
                effect="dark"
                v-if="row.node > 0"
                :type="nodeMap[row.node].type"
                size="mini"
                style="margin: 2px;"
              >
                {{ nodeMap[row.node].text }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="level" label="等级" :width="colWidth('56px', '52px', '42px')">
          <template slot-scope="{ row }">
            <el-tag :type="getLevelTagType(row.level)" v-if="row.level">
              {{ row.level }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="totalScore" :sortable="'custom'" label="总分" :width="colWidth('72px', '64px', '48px')" />
        <el-table-column
          prop="matchedConcepts"
          label="概念详情"
          :min-width="conceptColumnMinWidth"
          class-name="gap-up-concepts-col"
        >
          <template slot-scope="{ row }">
            <div
              class="gap-up-concept-tags"
              :class="{ 'gap-up-concept-tags--h5-strip': isMobile }"
            >
              <el-tag
                v-for="(conceptData, index) in row.matchedConcepts"
                :key="conceptData.name + index"
                :type="getMatchedTagType(conceptData.type)"
                size="mini"
              >
                {{ conceptData.name }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
      </el-table>
      </div>
    </div>
    <el-dialog title="过滤配置" :visible.sync="filterConfigVisible" width="560px" append-to-body>
      <p class="tip">
        短 / 中 / 长分界值中，<b>中期</b>用于四档点选：C0 / C1 / C2(13–20%，含20) / 无档(&gt;20% 及无值等)；C0/C1 分界默认 5 / 10。
      </p>
      <div v-for="item in rangeFields" :key="item.key" class="band-row">
        <span class="band-idx">{{ item.label }}</span>
        <el-input v-model="rangeDraft[item.key].a" placeholder="a" size="mini" style="width: 80px" />
        <span class="band-sep">/</span>
        <el-input v-model="rangeDraft[item.key].b" placeholder="b" size="mini" style="width: 80px" />
        <span class="band-sep">/</span>
        <el-input v-model="rangeDraft[item.key].c" placeholder="c" size="mini" style="width: 80px" />
        <span class="unit">%</span>
      </div>
      <p class="tip" style="margin-top: 16px">
        分段斜率（展示为原始值×1000%）：勾选后剔除斜率 &gt; 该阈值的票（默认 10，对应原始 0.01）。
      </p>
      <div class="band-row">
        <span class="band-idx">剔除&gt;</span>
        <el-input v-model="rangeDraft.slopeMin" placeholder="10" size="mini" style="width: 120px" />
        <span class="unit">%（×1000）</span>
      </div>
      <p class="tip" style="margin-top: 16px">
        距年高涨幅：勾选后保留「(年高-收盘)/收盘×100」≤ 该阈值的票（默认 15）。
      </p>
      <div class="band-row">
        <span class="band-idx">距年高≤</span>
        <el-input v-model="rangeDraft.distToYearHighMax" placeholder="15" size="mini" style="width: 120px" />
        <span class="unit">%</span>
      </div>
      <p class="tip" style="margin-top: 16px">
        综合分（竞价TOP盘前初筛：不计二/三板绝杀，昨竞实换=当日）：勾选后保留 ≥ 该阈值（默认 45）。
      </p>
      <div class="band-row">
        <span class="band-idx">综合分≥</span>
        <el-input v-model="rangeDraft.preScreenScoreMin" placeholder="45" size="mini" style="width: 120px" />
      </div>
      <p class="tip" style="margin-top: 16px">
        自由流通市值：勾选后保留自由流通市值 ≤ 该阈值的票（亿，默认 70）。
      </p>
      <div class="band-row">
        <span class="band-idx">市值≤</span>
        <el-input v-model="rangeDraft.marketCapMax" placeholder="70" size="mini" style="width: 120px" />
        <span class="unit">亿</span>
      </div>
      <p class="tip" style="margin-top: 16px">
        首板价：勾选后剔除首板日收盘价 &gt; 该阈值的票（元，默认 20）。
      </p>
      <div class="band-row">
        <span class="band-idx">首板≤</span>
        <el-input v-model="rangeDraft.firstBoardPriceMax" placeholder="20" size="mini" style="width: 120px" />
        <span class="unit">元</span>
      </div>
      <div slot="footer">
        <el-button size="small" @click="resetFilterConfig">恢复默认</el-button>
        <el-button size="small" @click="filterConfigVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveFilterConfig">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { stockApi } from '@/api/stockApi';
import { formatDate } from '@/utils/index'
import { nodeMap, consecutiveBoardsMap } from './assets/js/config'
import { BREAKPOINTS } from '@/utils/device'
import { CONCEPT_SOURCE_MERGE } from '@/utils/conceptSource'
import { resolveAnalysisConcepts } from '@/utils/conceptFilter'
import { passesMarketCapFilter, passesFirstBoardPriceFilter, isMainBoard, isChiNextBoard, passesBoardTierFilter, BOARD_TIER_BUTTON_DEFS } from '@/utils/limitUpFilters'
import { passesMa250Filter } from '@/views/GapUpAnalysis/assets/js/ma250Filter'
import {
  RANGE_FIELDS,
  loadRangeConfig,
  saveRangeConfig,
  cloneRangeConfig,
  defaultRangeConfig,
  midChipTierButtonDefs,
  passesMidChipTierFilter,
  parsePct,
  formatSlopeX1000Pct,
  slopeThresholdRaw
} from '@/utils/hfbChipFilters'
import {
  loadGapUpLogicByCode,
  saveGapUpLogicEntry,
  saveGapUpLogicMap,
  formatGapUpLogicDisplay,
  hasGapUpLogicMatch
} from '@/utils/gapUpLogicMatch'
import {
  CHIP_REVIEW_TIER_BUTTONS,
  attachChipReviewScoreV1,
  passesChipReviewTierFilter
} from '@/utils/chipReviewScoreV1'

const DAY_NODE_SNAPSHOT_FIELDS = [
  { key: 'temperature', label: '温度', kind: 'int' },
  { key: 'boardHeight', label: '连板高度', kind: 'int' },
  { key: 'limitUpCount', label: '涨停家数', kind: 'int' },
  { key: 'limitDownCount', label: '跌停家数', kind: 'int' },
  { key: 'firstBoardCount', label: '首板数', kind: 'int' },
  { key: 'secondBoardCount', label: '二板数', kind: 'int' },
  { key: 'thirdBoardCount', label: '三板数', kind: 'int' },
  { key: 'highBoardCount', label: '高度板数', kind: 'int' },
  { key: 'secondPromoteRate', label: '二板晋级率', kind: 'pct' },
  { key: 'thirdPromoteRate', label: '三板晋级率', kind: 'pct' },
  { key: 'highPromoteRate', label: '高度晋级率', kind: 'pct' },
  { key: 'breakLimitRate', label: '破板率', kind: 'pct' },
  { key: 'yestLimitPerf', label: '昨涨停表现', kind: 'pct' },
  { key: 'yestBoardPerf', label: '昨连板表现', kind: 'pct' },
  { key: 'yestBreakPerf', label: '昨破板表现', kind: 'pct' },
  { key: 'marketVolume', label: '量能', kind: 'num' },
  { key: 'ycln', label: '成交额(亿)', kind: 'num', optional: true },
  { key: 'marketIndex', label: '指数涨跌', kind: 'pct' }
]

export default {
  name: 'GapUpAnalysis',
  props: {
    /** 龙头指挥中心内嵌：只展示结果筛选与表格 */
    embedResults: { type: Boolean, default: false },
    /** 指挥中心顶部固定页：筛选+表格，日期跟随指挥中心，剔除各王国接力池 */
    lccBoard: { type: Boolean, default: false },
    /** 内嵌时跟随指挥中心交易日 */
    tradeDate: { type: String, default: '' },
    /** 内嵌时用指挥中心当前龙头做龙头框默认匹配 */
    embedLeader: { type: Object, default: null },
    embedDragonCards: { type: Array, default: () => [] },
    embedDragonSelected: { type: Array, default: () => [] },
    /** 内嵌点选：今日补涨接力 TOP 池 */
    embedPickActive: { type: Boolean, default: false },
    embedPickedCodes: { type: Array, default: () => [] },
    /** 内嵌主属性：与大界面点选概念相同，每个命中概念 +5 */
    embedHighlightConcepts: { type: Array, default: () => [] }
  },
  data() {
    const rangeConfig = loadRangeConfig()
    return {
      isMobile: false,
      isTablet: false,
      showContent: false,
      formData: {
        date: (this.embedResults || this.lccBoard) && this.tradeDate ? this.tradeDate : formatDate(),
      },
      daySoil: '',
      relayExcludeCodes: [],
      conceptSource: CONCEPT_SOURCE_MERGE,
      // 基础过滤默认开启
      onlyMainBoard: true,
      onlyChiNext: false,
      filterMarketValue: true,
      filterFirstBoardPrice: true,
      filterMa250: true,
      filterSlope: true,
      filterExcludeRelayTop: true,
      syncThsLoading: false,
      syncAllDragonsLoading: false,
      viewBaseFilteredOut: false,
      filterDistToYearHigh: false,
      filterPreScreenScore: false,
      rangeFields: RANGE_FIELDS,
      rangeConfig,
      rangeDraft: cloneRangeConfig(rangeConfig),
      filterConfigVisible: false,
      filterMidChipTiers: [],
      filterBoardTiers: [],
      /** 空数组 = 全部；多选 OR：S+ / S / A / B */
      filterChipReviewTiers: [],
      sortProp: 'consecutiveBoards', // 初始按连板高度排序
      sortOrder: 'descending', // 降序
      hasAnalyzed: false, // 标记是否执行过查询
      // 今日涨停的股票
      limitUpStocks: [],
      // 补涨分析后的数组
      analyzeLimitUpStocks: [],
      // 搜索相关
      selectedStocks: [], // 选择的股票
      stockOptions: [],
      searchLoading: false,
      // 涨幅TOP
      selectedTopStocks: [],
      topStockOptions: [],
      // 龙头+涨幅TOP
      allSelectedStocks: [],
      nodeMap,
      consecutiveBoardsMap,
      // 概念相关
      newConcept: '',
      customConcepts: [],
      conceptOptions: [],
      conceptSearchLoading: false,
      conceptsList: [],
      highlightedConcepts: [], // 高亮的概念
      /** 概念列表折叠：空列表默认收起，避免占白 */
      conceptsCollapsed: true,
      // 逻辑匹配（按 ts_code 存储，同日 session 持久化）
      logicMatchByCode: {},
      /** 当日 AuctionTopLibrary：短代码 -> 旗数（T0=2 / T1=1） */
      topLibFlagsByCode: {},
      
      // 分析相关
      analysisResults: [],
      nextDayLoading: false,
      syncingDayData: false,
      dayNodeScore: null,
      dayNodeHit: false,
      dayNodeHits: [],
      dayNodeDailyDoc: null,
      dayNodeFactorSource: '',
      greedyNodeScheme: 'V5F',
      greedyNodeThreshold: 7,
      greedyNodeCatalog: []
    };
  },
  computed: {
    greedyNodeSchemeTitle() {
      const scheme = this.greedyNodeScheme || 'V5F'
      const thr = this.greedyNodeThreshold ?? 7
      const n = (this.greedyNodeCatalog || []).length
      return `${scheme} · 阈值≥${thr}${n ? ` · ${n}节点` : ''}`
    },
    greedyNodeCatalogMap() {
      const map = {}
      for (const n of this.greedyNodeCatalog || []) {
        map[n.id] = n
      }
      return map
    },
    dayNodeSnapshotRows() {
      const doc = this.dayNodeDailyDoc || {}
      return DAY_NODE_SNAPSHOT_FIELDS.map((f) => {
        const raw = doc[f.key]
        const empty = raw == null || raw === ''
        return {
          key: f.key,
          label: f.label,
          optional: !!f.optional,
          missing: f.optional ? false : empty,
          display: empty ? '—' : this.formatDayNodeSnapshotValue(f, raw)
        }
      })
    },
    dayNodeSnapshotTotal() {
      return DAY_NODE_SNAPSHOT_FIELDS.filter((f) => !f.optional).length
    },
    dayNodeSnapshotFilled() {
      return this.dayNodeSnapshotRows.filter((r) => !r.optional && !r.missing).length
    },
    dayNodeSnapshotMissing() {
      return this.dayNodeSnapshotRows.filter((r) => r.missing)
    },
    dayNodeSnapshotComplete() {
      return this.dayNodeSnapshotMissing.length === 0
    },
    dayNodeFactorSourceLabel() {
      const s = String(this.dayNodeFactorSource || '').trim()
      if (s === 'manual') return '手改'
      if (s === 'realtime') return '实时'
      if (s === 'expression') return 'Expression'
      return s || ''
    },
    // 计算属性：获取带计数的概念数组
    allConceptsList() {
      return [...this.customConcepts, ...this.conceptsList]
    },
    tableSourceList() {
      const raw =
        this.analyzeLimitUpStocks.length > 0
          ? this.analyzeLimitUpStocks
          : this.limitUpStocks;
      return (raw || []).map((stock) => attachChipReviewScoreV1(stock));
    },
    hasBaseFiltersActive() {
      return (
        this.onlyMainBoard ||
        this.onlyChiNext ||
        this.filterMarketValue ||
        this.filterFirstBoardPrice ||
        this.filterMa250 ||
        this.filterSlope
      );
    },
    baseFilteredOutCount() {
      if (!this.hasBaseFiltersActive) return 0;
      return this.tableSourceList.filter((stock) => !this.passesBaseFilters(stock)).length;
    },
    /** 其它筛选生效后、筹码梯队筛选前的列表（用于梯队数量） */
    listBeforeChipReviewTierFilter() {
      let list = this.tableSourceList;
      if (this.viewBaseFilteredOut) {
        if (!this.hasBaseFiltersActive) return [];
        return this.gateEmbedScore(this.dropRelayPool(list.filter((stock) => !this.passesBaseFilters(stock))));
      }
      if (this.hasBaseFiltersActive) {
        list = list.filter((stock) => this.passesBaseFilters(stock));
      }
      if (this.hasOptionalFiltersExceptChipReview) {
        list = list.filter((stock) => this.passesOptionalFiltersExceptChipReview(stock));
      }
      return this.gateEmbedScore(this.dropRelayPool(list));
    },
    chipReviewTierCounts() {
      const counts = { ALL: 0, 'S+': 0, S: 0, A: 0, B: 0 };
      const list = this.listBeforeChipReviewTierFilter;
      counts.ALL = list.length;
      for (const row of list) {
        const t = row.chipReviewTier;
        if (t && counts[t] != null) counts[t] += 1;
      }
      return counts;
    },
    hasOptionalFiltersExceptChipReview() {
      return (
        this.filterDistToYearHigh ||
        this.filterPreScreenScore ||
        (this.filterMidChipTiers && this.filterMidChipTiers.length) ||
        (this.filterBoardTiers && this.filterBoardTiers.length)
      );
    },
    hasChipReviewTierFilter() {
      return Array.isArray(this.filterChipReviewTiers) && this.filterChipReviewTiers.length > 0;
    },
    // 根据过滤条件计算显示的列表
    showTableList() {
      let list = this.tableSourceList;

      if (this.viewBaseFilteredOut) {
        if (!this.hasBaseFiltersActive) return [];
        list = list.filter((stock) => !this.passesBaseFilters(stock));
        return this.sortTableData(this.gateEmbedScore(this.dropRelayPool(list)));
      }

      if (this.hasBaseFiltersActive) {
        list = list.filter((stock) => this.passesBaseFilters(stock));
      }

      const hasOptionalFilters =
        this.hasOptionalFiltersExceptChipReview || this.hasChipReviewTierFilter;

      if (!hasOptionalFilters) {
        return this.sortTableData(this.gateEmbedScore(this.dropRelayPool(list)));
      }

      list = list.filter((stock) => this.passesOptionalFilters(stock));
      return this.sortTableData(this.gateEmbedScore(this.dropRelayPool(list)));
    },
    chipReviewTierButtons() {
      return CHIP_REVIEW_TIER_BUTTONS;
    },
    /** 列前三排名规则：asc=越小越好，desc=越大越好 */
    columnRankSpecs() {
      return [
        { key: 'distToYearHighPct', order: 'asc' },
        { key: 'shortChip', order: 'asc' },
        { key: 'midChip', order: 'asc' },
        { key: 'longChip', order: 'asc' },
        { key: 'chipScoreSum', order: 'asc' },
        { key: 'mainNetInflow0', order: 'desc' },
        { key: 'netInflowToAmount0', order: 'desc' },
        { key: 'marketValue', order: 'asc' }
      ];
    },
    /** 当前展示列表各列前三 */
    displayColumnRanks() {
      const list = this.showTableList;
      const ranks = {};
      for (const spec of this.columnRankSpecs) {
        const sorted = list
          .map((row) => ({ row, v: Number(row[spec.key]) }))
          .filter((x) => Number.isFinite(x.v))
          .sort((a, b) => (spec.order === 'asc' ? a.v - b.v : b.v - a.v));
        for (let i = 0; i < Math.min(3, sorted.length); i++) {
          const code = sorted[i].row.ts_code || sorted[i].row.code;
          if (code) ranks[`${code}|${spec.key}`] = i + 1;
        }
      }
      return ranks;
    },
    slopeMinDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.slopeMin);
      return Number.isFinite(n) ? n : 10;
    },
    slopeMinValue() {
      const n = Number(this.rangeConfig && this.rangeConfig.slopeMin);
      return Number.isFinite(n) ? n : 10;
    },
    distToYearHighMaxDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.distToYearHighMax);
      return Number.isFinite(n) ? n : 15;
    },
    distToYearHighMaxValue() {
      const n = Number(this.rangeConfig && this.rangeConfig.distToYearHighMax);
      return Number.isFinite(n) ? n : 15;
    },
    preScreenScoreMinDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.preScreenScoreMin);
      return Number.isFinite(n) ? String(n) : '45';
    },
    preScreenScoreMinValue() {
      const n = Number(this.rangeConfig && this.rangeConfig.preScreenScoreMin);
      return Number.isFinite(n) ? n : 45;
    },
    marketCapMaxDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.marketCapMax);
      return Number.isFinite(n) ? n : 70;
    },
    marketCapMaxValue() {
      return this.marketCapMaxDisplay;
    },
    firstBoardPriceMaxDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.firstBoardPriceMax);
      return Number.isFinite(n) ? n : 20;
    },
    firstBoardPriceMaxValue() {
      return this.firstBoardPriceMaxDisplay;
    },
    midChipTierButtons() {
      return midChipTierButtonDefs(this.rangeConfig.mid);
    },
    boardTierButtons() {
      return BOARD_TIER_BUTTON_DEFS;
    },
    isMini() {
      return this.isMobile ? 'mini' : ''
    },
    isComputer() {
      return !this.isMobile && !this.isTablet
    },
    /** H5 概念列收窄，在单元格内横滑；平板/桌面沿用原 min-width */
    conceptColumnMinWidth() {
      if (this.embedResults) return '64px'
      if (this.isMobile) return '400px'
      if (!this.isComputer) return '280px'
      return '160px'
    },
    embedHighlightKey() {
      return (this.embedHighlightConcepts || [])
        .map((s) => String(s || '').trim())
        .filter(Boolean)
        .join('\u0001')
    }
  },
  watch: {
    tradeDate(next) {
      if (!this.embedResults && !this.lccBoard) return
      const date = String(next || '').trim()
      if (!date || date === this.formData.date) return
      this.formData.date = date
      this.hasAnalyzed = false
      this.analyzeLimitUpStocks = []
      this.changeDate()
    },
    embedLeader: {
      handler(next, prev) {
        if (!this.embedResults) return
        const a = this.normCode6(next && (next.code || next.leaderId))
        const b = this.normCode6(prev && (prev.code || prev.leaderId))
        if (!a || a === b) return
        this.syncEmbedLeader()
      }
    },
    embedHighlightKey(next, prev) {
      if (!this.embedResults || next === prev || !this.hasAnalyzed) return
      this.analyzeGapUp()
    },
    allConceptsList: {
      handler(list, prev) {
        const n = (list && list.length) || 0
        const p = (prev && prev.length) || 0
        if (n === 0) {
          this.conceptsCollapsed = true
        } else if (p === 0) {
          // 空→有内容：自动展开，避免分析后仍看不到标签
          this.conceptsCollapsed = false
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleConceptsCollapsed() {
      this.conceptsCollapsed = !this.conceptsCollapsed
    },
    colWidth(desktop, compact, embed) {
      if (this.embedResults) return embed
      return this.isComputer ? desktop : compact
    },
    /** 切换页：勾选后剔除当日所有王国的补涨接力 TOP。 */
    dropRelayPool(list) {
      if (!this.lccBoard || !this.filterExcludeRelayTop || !(this.relayExcludeCodes || []).length) return list || [];
      const ban = new Set(this.relayExcludeCodes);
      return (list || []).filter((stock) => !ban.has(this.embedCode6(stock)));
    },
    collectKingdomDragonCodes() {
      const out = []
      const seen = new Set()
      const push = (code) => {
        const c = this.embedCode6({ code })
        if (!c || c === '000000' || seen.has(c)) return
        seen.add(c)
        out.push(c)
      }
      const roleOrder = { leader: 0, trend: 1, board: 2 }
      try {
        const hidden = localStorage.getItem('lcc.kingdom.hideCulture.v1') === '1'
        if (!hidden) {
          const text = localStorage.getItem('lcc.kingdom.roles.v1|culture')
          const raw = text == null || text === '' ? { '600825': 'leader', '601811': 'trend', '603096': 'board' } : JSON.parse(text)
          const rows = Object.keys(raw && typeof raw === 'object' ? raw : {})
            .map((code) => ({ code, role: raw[code] }))
            .filter((row) => roleOrder[row.role] != null)
            .sort((a, b) => roleOrder[a.role] - roleOrder[b.role])
          rows.forEach((row) => push(row.code))
        }
      } catch (err) {
        /* ignore */
      }
      let kingdoms = []
      try {
        const raw = JSON.parse(localStorage.getItem('lcc.kingdom.tabs.v1') || '[]')
        kingdoms = Array.isArray(raw) ? raw : []
      } catch (err) {
        kingdoms = []
      }
      kingdoms.forEach((kingdom) => {
        if (!kingdom || !kingdom.id) return
        const roles = { leader: '', trend: '', board: '', ...(kingdom.roles || {}) }
        let ov = null
        try {
          ov = JSON.parse(localStorage.getItem('lcc.kingdom.roleOverride.v1|' + kingdom.id) || 'null')
        } catch (err) {
          ov = null
        }
        const roleKeys = ['leader', 'trend', 'board']
        roleKeys.forEach((key) => {
          const slot = ov && ov[key]
          if (slot && slot.cleared) return
          const code = slot && slot.code ? slot.code : roles[key]
          push(code)
        })
      })
      return out
    },
    collectFourBoardCodes() {
      const rows = (this.limitUpStocks || []).slice()
      rows.sort((a, b) => {
        const ab = Number(a && a.consecutiveBoards) || 0
        const bb = Number(b && b.consecutiveBoards) || 0
        if (ab !== bb) return bb - ab
        return String((a && a.name) || '').localeCompare(String((b && b.name) || ''), 'zh')
      })
      const out = []
      const seen = new Set()
      rows.forEach((row) => {
        const boards = Number(row && row.consecutiveBoards) || 0
        if (boards < 4) return
        const code = this.embedCode6(row)
        if (!code || code === '000000' || seen.has(code)) return
        seen.add(code)
        out.push(code)
      })
      return out
    },
    async syncAllDragonsToThs() {
      if (this.syncAllDragonsLoading || this.syncThsLoading) return
      const dragons = this.collectKingdomDragonCodes()
      const fourBoards = this.collectFourBoardCodes().filter((code) => dragons.indexOf(code) < 0)
      const codes = dragons.concat(fourBoards)
      if (!codes.length) {
        this.$message.warning('没有可同步的龙头或4板股票')
        return
      }
      this.syncAllDragonsLoading = true
      try {
        const res = await stockApi.syncAllDragonsToThs(codes)
        if (res?.authError) {
          this.$message.warning(res?.message || '同花顺 Cookie 已失效，请更新 THS_COOKIE')
          return
        }
        if (res?.thsEnabled === false) {
          this.$message.warning(res?.message || '未配置 THS_COOKIE')
          return
        }
        if (res?.partial) {
          this.$message.warning(res?.message || '部分账号同步失败')
          return
        }
        if (!res?.ok) {
          this.$message.error(res?.message || '同步失败')
          return
        }
        this.$message.success(`${res?.message || '同步成功'}（龙头${dragons.length}只，另有≥4板${fourBoards.length}只）`)
      } catch (err) {
        this.$message.error((err && err.message) || '同步失败')
      } finally {
        this.syncAllDragonsLoading = false
      }
    },
    async syncGapBoardToThs() {
      if (this.syncThsLoading) return
      if (this.viewBaseFilteredOut) {
        this.$message.warning('请先关闭「查看被过滤」再同步当前列表')
        return
      }
      const date = String(this.formData.date || '').trim()
      const codes = (this.showTableList || [])
        .map((row) => this.embedCode6(row))
        .filter((code) => code && code !== '000000')
      if (!date || !codes.length) {
        this.$message.warning('当前没有可同步的股票')
        return
      }
      this.syncThsLoading = true
      try {
        const res = await stockApi.syncGapBoardToThs(date, codes)
        if (res?.authError) {
          this.$message.warning(res?.message || '同花顺 Cookie 已失效，请更新 THS_COOKIE')
          return
        }
        if (res?.thsEnabled === false) {
          this.$message.warning(res?.message || '未配置 THS_COOKIE')
          return
        }
        if (res?.partial) {
          this.$message.warning(res?.message || '部分账号同步失败')
          return
        }
        if (!res?.ok) {
          this.$message.error(res?.message || '同步失败')
          return
        }
        this.$message.success(res?.message || '同步成功')
      } catch (err) {
        this.$message.error((err && err.message) || '同步失败')
      } finally {
        this.syncThsLoading = false
      }
    },
    loadRelayExclude() {
      if (!this.lccBoard || !this.filterExcludeRelayTop) {
        this.relayExcludeCodes = [];
        return;
      }
      const td = String(this.formData.date || '').trim();
      const prefix = `lcc.themeProp.relayTop.v3|${td}|`;
      const codes = [];
      const seen = new Set();
      try {
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (!key || key.indexOf(prefix) !== 0) continue;
          const rows = JSON.parse(localStorage.getItem(key) || '[]');
          (Array.isArray(rows) ? rows : []).forEach((row) => {
            const code = this.embedCode6(row);
            if (!code || code === '000000' || seen.has(code)) return;
            seen.add(code);
            codes.push(code);
          });
        }
      } catch (err) {
        this.relayExcludeCodes = codes;
        return;
      }
      this.relayExcludeCodes = codes;
    },
    /** 指挥中心内嵌：总分 < 2 不展示 */
    gateEmbedScore(list) {
      if (!this.embedResults || !this.hasAnalyzed) return list || []
      return (list || []).filter((stock) => {
        const n = Number(stock && stock.totalScore)
        return Number.isFinite(n) && n >= 2
      })
    },
    embedCode6(row) {
      return String((row && (row.ts_code || row.code)) || '')
        .trim()
        .split('.')[0]
        .replace(/\D/g, '')
        .padStart(6, '0')
        .slice(-6)
    },
    isEmbedPicked(row) {
      const code = this.embedCode6(row)
      if (!code || code === '000000') return false
      return (this.embedPickedCodes || []).some((c) => this.embedCode6({ code: c }) === code)
    },
    emitAutoRelay(stocks) {
      if (!this.embedResults) return
      const attrs = new Set(
        (this.embedHighlightConcepts || []).map((name) => String(name || '').trim()).filter(Boolean)
      )
      if (!attrs.size) return
      const rows = []
      const list = stocks || []
      for (let i = 0; i < list.length; i += 1) {
        const stock = list[i]
        const highlighted = Number(stock && stock.highlightedConceptScore)
        if (!Number.isFinite(highlighted) || highlighted <= 0) continue
        const code = this.embedCode6(stock)
        if (!code || code === '000000') continue
        const concepts = Array.isArray(stock.matchedConcepts)
          ? stock.matchedConcepts.map((item) => ({
            name: item && item.name,
            type: item && item.type,
            score: item && item.score
          }))
          : []
        rows.push({
          code,
          name: stock.name || '',
          midChip: stock.midChip,
          rangeAllD0: stock.rangeAllD0,
          marketValue: stock.marketValue,
          boardLabel: this.formatConsecutiveBoards(stock),
          conceptScoreSum: this.embedConceptScoreSum(stock),
          matchedConcepts: concepts,
          preScreenScore: stock.preScreenScore,
          nodeScore: stock.nodeScore,
          firstBoardDate: stock.firstBoardDate
        })
      }
      if (rows.length) this.$emit('auto-relay', rows)
    },
    embedConceptScoreSum(row) {
      const parts = [row && row.conceptMatchScore, row && row.highlightedConceptScore, row && row.repeatConceptScore]
      let sum = 0
      let any = false
      parts.forEach((v) => {
        const n = Number(v)
        if (!Number.isFinite(n)) return
        sum += n
        any = true
      })
      return any ? Math.round(sum * 100) / 100 : null
    },
    embedRowClassName({ row }) {
      if (!this.embedResults || !this.embedPickActive || !this.isEmbedPicked(row)) return ''
      return 'is-embed-picked'
    },
    onEmbedTableRowClick(row, _column, event) {
      if (!this.embedResults || !this.embedPickActive || !row) return
      const el = event && event.target
      if (el && el.closest && el.closest('button, a, .el-button, .el-checkbox')) return
      this.toggleEmbedPick(row)
    },
    toggleEmbedPick(row) {
      if (!this.embedResults || !row) return
      const code = this.embedCode6(row)
      if (!code || code === '000000') return
      const concepts = Array.isArray(row.matchedConcepts)
        ? row.matchedConcepts.map((c) => ({
          name: c && c.name,
          type: c && c.type,
          score: c && c.score
        }))
        : []
      this.$emit('embed-pick', {
        code,
        name: row.name || '',
        midChip: row.midChip,
        rangeAllD0: row.rangeAllD0,
        marketValue: row.marketValue,
        boardLabel: this.formatConsecutiveBoards(row),
        conceptScoreSum: this.embedConceptScoreSum(row),
        matchedConcepts: concepts,
        preScreenScore: row.preScreenScore,
        nodeScore: row.nodeScore,
        firstBoardDate: row.firstBoardDate
      })
    },
    formatChipPct(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-';
      const n = Number(cellValue);
      if (!Number.isFinite(n)) return '-';
      return `${(n * 100).toFixed(2)}%`;
    },
    fmtChipV2Score(row) {
      const s = Number(row?.chipV2Score);
      if (!Number.isFinite(s)) return '-';
      const tag = s >= 90 ? '强' : s >= 70 ? '中' : '弱';
      return `${s}｜${tag}`;
    },
    fmtRangeAllD0(_r, _c, v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return `${(n * 100).toFixed(1)}%`;
    },
    fmtPriceToMaxWithLabel(row) {
      const v = row?.priceToMax;
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      const sign = n > 0 ? '+' : '';
      const pct = `${sign}${(n * 100).toFixed(1)}%`;
      const label = row?.pricePositionLabel ? ` ${row.pricePositionLabel}` : '';
      return `${pct}${label}`;
    },
    openFilterConfig() {
      this.rangeDraft = cloneRangeConfig(this.rangeConfig);
      this.filterConfigVisible = true;
    },
    resetFilterConfig() {
      this.rangeDraft = defaultRangeConfig();
    },
    saveFilterConfig() {
      const next = cloneRangeConfig(this.rangeDraft);
      for (const f of RANGE_FIELDS) {
        const aPct = parsePct(next[f.key].a);
        const bPct = parsePct(next[f.key].b);
        const cPct = parsePct(next[f.key].c);
        if (aPct == null || bPct == null || cPct == null) {
          this.$message.warning(`${f.label}请填写三个分界值`);
          return;
        }
        if (!(aPct < bPct && bPct < cPct)) {
          this.$message.warning(`${f.label}须满足 a < b < c`);
          return;
        }
      }
      const slopeMin = Number(next.slopeMin);
      if (!Number.isFinite(slopeMin)) {
        this.$message.warning('分段斜率阈值须为数字');
        return;
      }
      const distMax = Number(next.distToYearHighMax);
      if (!Number.isFinite(distMax)) {
        this.$message.warning('距年高阈值须为数字');
        return;
      }
      const preMin = Number(next.preScreenScoreMin);
      if (!Number.isFinite(preMin)) {
        this.$message.warning('综合分阈值须为数字');
        return;
      }
      const capMax = Number(next.marketCapMax);
      if (!Number.isFinite(capMax) || capMax <= 0) {
        this.$message.warning('自由流通市值上限须为正数');
        return;
      }
      const fbMax = Number(next.firstBoardPriceMax);
      if (!Number.isFinite(fbMax) || fbMax <= 0) {
        this.$message.warning('首板价上限须为正数');
        return;
      }
      next.slopeMin = String(slopeMin);
      next.slopeUnit = 'x1000pct';
      next.distToYearHighMax = String(distMax);
      next.preScreenScoreMin = String(preMin);
      next.marketCapMax = String(capMax);
      next.firstBoardPriceMax = String(fbMax);
      this.rangeConfig = next;
      saveRangeConfig(next);
      this.filterConfigVisible = false;
      this.$message.success('过滤配置已保存');
    },
    toggleChipTier(field, tierKey) {
      const cur = Array.isArray(this[field]) ? [...this[field]] : [];
      const i = cur.indexOf(tierKey);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(tierKey);
      this[field] = cur;
    },
    isChipReviewTierActive(key) {
      if (key === 'ALL') return !this.hasChipReviewTierFilter;
      return this.filterChipReviewTiers.includes(key);
    },
    toggleChipReviewTier(key) {
      if (key === 'ALL') {
        this.filterChipReviewTiers = [];
        return;
      }
      const cur = Array.isArray(this.filterChipReviewTiers) ? [...this.filterChipReviewTiers] : [];
      const i = cur.indexOf(key);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(key);
      this.filterChipReviewTiers = cur;
    },
    chipReviewTierCountLabel(key) {
      const n = this.chipReviewTierCounts[key];
      return n == null ? '' : String(n);
    },
    chipReviewCellClass(row) {
      const t = row && row.chipReviewTier;
      if (t === 'S+') return 'chip-review-cell--splus';
      if (t === 'S') return 'chip-review-cell--s';
      if (t === 'A') return 'chip-review-cell--a';
      if (t === 'B') return 'chip-review-cell--b';
      return '';
    },
    passesBaseFilters(stock) {
      if (this.onlyMainBoard && !isMainBoard(stock.ts_code)) return false;
      if (this.onlyChiNext && !isChiNextBoard(stock.ts_code)) return false;
      if (this.filterMarketValue && !passesMarketCapFilter(stock, this.marketCapMaxValue)) return false;
      if (this.filterFirstBoardPrice && !passesFirstBoardPriceFilter(stock, this.firstBoardPriceMaxValue)) {
        return false;
      }
      if (this.filterMa250) {
        if (
          !passesMa250Filter({
            firstBoardPrice: stock.firstBoardPrice ?? stock.closePrice,
            maMax: stock.maMax,
            ma10: stock.ma10,
            ma20: stock.ma20,
            ma30: stock.ma30,
            ma40: stock.ma40,
            ma60: stock.ma60,
            ma110: stock.ma110,
            ma120: stock.ma120,
            ma160: stock.ma160,
            ma210: stock.ma210,
            ma250: stock.ma250
          })
        ) {
          return false;
        }
      }
      if (this.filterSlope) {
        const thr = slopeThresholdRaw(this.slopeMinValue);
        const v = Number(stock.chipSlope);
        if (Number.isFinite(v) && v > thr) return false;
      }
      return true;
    },
    passesOptionalFiltersExceptChipReview(stock) {
      if (this.filterDistToYearHigh) {
        const thr = this.distToYearHighMaxValue;
        const v = Number(stock.distToYearHighPct);
        if (!Number.isFinite(v) || v > thr) return false;
      }
      if (this.filterPreScreenScore) {
        const thr = this.preScreenScoreMinValue;
        const v = Number(stock.preScreenScore);
        if (!Number.isFinite(v) || v < thr) return false;
      }
      if (!passesMidChipTierFilter(stock.midChip, this.filterMidChipTiers, this.rangeConfig.mid)) return false;
      if (!passesBoardTierFilter(stock, this.filterBoardTiers)) return false;
      return true;
    },
    passesOptionalFilters(stock) {
      if (!this.passesOptionalFiltersExceptChipReview(stock)) return false;
      if (!passesChipReviewTierFilter(stock, this.filterChipReviewTiers)) return false;
      return true;
    },
    fmtSlope(_r, _c, v) {
      return formatSlopeX1000Pct(v);
    },
    fmtDistYearHigh(_r, _c, v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return `${n.toFixed(2)}%`;
    },
    fmtPreScreenScore(_r, _c, v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return n.toFixed(1);
    },
    fmtYi(_r, _c, v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return Number((n / 1e8).toFixed(2));
    },
    fmtRatioPct(_r, _c, v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return `${(n * 100).toFixed(2)}%`;
    },
    fmtMarketValueInt(_r, _c, v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return String(Math.round(n));
    },
    tableCellClassName({ row, column }) {
      const prop = column.property;
      if (!this.columnRankSpecs.some((s) => s.key === prop)) return '';
      const code = row.ts_code || row.code;
      const rank = this.displayColumnRanks[`${code}|${prop}`];
      if (rank === 1) return 'gap-cell-rank-1';
      if (rank === 2 || rank === 3) return 'gap-cell-rank-2';
      return '';
    },
    formatConsecutiveBoards(row) {
      if (row?.consecutiveBoardsLabel) return row.consecutiveBoardsLabel;
      return this.consecutiveBoardsMap[row?.consecutiveBoards] || row?.consecutiveBoards || '-';
    },
    nodeScoreColor(score) {
      const s = Number(score);
      if (!Number.isFinite(s) || s < 7) return null;
      const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
      const lerp = (a, b, t) => a + (b - a) * t;
      const t = clamp((s - 7) / 15, 0, 1);
      const sat = Math.round(lerp(82, 88, t));
      const light = Math.round(lerp(42, 24, t));
      return `hsl(180, ${sat}%, ${light}%)`;
    },
    nodeStarStyle(score) {
      const c = this.nodeScoreColor(score);
      return c ? { color: c } : null;
    },
    nodeStarTitle(row) {
      const s = Number(row && row.nodeScore);
      const fb = row && row.firstBoardDate ? `首板日 ${row.firstBoardDate}` : '首板日';
      const score = Number.isFinite(s) ? `节点分 ${s}` : '节点分';
      return `${fb} · ${score} · ≥7`;
    },
    soilBoardMark(row) {
      if (this.daySoil !== 'S' && this.daySoil !== 'S_PLUS') return '';
      const n = Number(row && row.consecutiveBoards);
      const label = this.formatConsecutiveBoards(row);
      const firstOrSecond = n === 1 || n === 2 || label === '首板' || label === '二板';
      if (!firstOrSecond) return '';
      return this.daySoil === 'S_PLUS' ? '★★S+' : '★★S';
    },
    async loadDaySoil() {
      const date = String(this.formData.date || '').trim();
      this.daySoil = '';
      if (!date) return;
      try {
        const data = await stockApi.getRelayEcologyCurve(date, 5);
        const series = Array.isArray(data && data.series) ? data.series : [];
        const resolved = String((data && data.resolvedDate) || '').trim();
        const hit = series.find((p) => p && (p.date === date || (resolved && p.date === resolved))) || null;
        const state = hit && hit.soilState;
        this.daySoil = state === 'S' || state === 'S_PLUS' ? state : '';
      } catch (err) {
        this.daySoil = '';
      }
    },
    async attachNodeScores(rows) {
      const list = Array.isArray(rows) ? rows : [];
      const dates = [...new Set(list.map((r) => String((r && r.firstBoardDate) || '').trim()).filter(Boolean))];
      const byDate = {};
      await Promise.all(dates.map(async (d) => {
        try {
          const doc = await stockApi.fetchDailyData(d);
          const s = doc && doc.greedyNodeScore;
          byDate[d] = s != null && s !== '' && Number.isFinite(Number(s)) ? Number(s) : null;
        } catch (err) {
          byDate[d] = null;
        }
      }));
      return list.map((r) => {
        const fb = String((r && r.firstBoardDate) || '').trim();
        const ns = fb && Object.prototype.hasOwnProperty.call(byDate, fb) ? byDate[fb] : null;
        return { ...r, nodeScore: ns, nodeHit: ns != null && ns >= 7 };
      });
    },
    formatLimitTime(t) {
      if (!t) return '-';
      const s = String(t).trim();
      const full = s.match(/(\d{1,2}:\d{2}:\d{2})/);
      if (full) return full[1];
      const short = s.match(/(\d{1,2}:\d{2})/);
      return short ? short[1] : s.slice(0, 8);
    },
    async handleSyncDayData() {
      if (!this.formData.date) {
        this.$message.warning('请先选择日期');
        return;
      }
      this.syncingDayData = true;
      try {
        const result = await stockApi.syncGapUpDayData(this.formData.date);
        if (!result?.ok) {
          throw new Error(result?.message || '获取数据失败');
        }
        const limitUpCount = Number(result?.limitUpCount || 0);
        const stockDailyCount = Number(result?.stockDailyCount || 0);
        this.$message.success(`获取完成：涨停 ${limitUpCount} 条，日线 ${stockDailyCount} 条`);
        this.hasAnalyzed = false;
        this.analyzeLimitUpStocks = [];
        await this.changeDate();
        await this.loadDayNodeScore();
      } catch (error) {
        console.error('按日期获取涨停数据失败:', error);
        this.$message.error('按日期获取涨停数据失败');
      } finally {
        this.syncingDayData = false;
      }
    },
    async handleNextTradeDay() {
      if (!this.formData.date) {
        this.$message.warning('请选择日期')
        return
      }
      this.nextDayLoading = true
      try {
        const res = await stockApi.nextHistoryFirstBoardTradeDate(this.formData.date)
        if (!res?.ok || !res.nextTradeDate) {
          this.$message.warning(res?.message || '无下一交易日')
          return
        }
        this.formData.date = res.nextTradeDate
        this.hasAnalyzed = false
        this.analyzeLimitUpStocks = []
        await this.changeDate()
      } catch (e) {
        this.$message.error(e?.message || '获取下一交易日失败')
      } finally {
        this.nextDayLoading = false
      }
    },
    checkScreenSize() {
      const width = window.innerWidth;
      this.isMobile = width <= BREAKPOINTS.MOBILE;
      this.isTablet = width > BREAKPOINTS.MOBILE && width <= BREAKPOINTS.TABLET;
      this.showContent = true
    },
    // 获取涨停的股票和概念（无当日数据时后端会回退到最近有涨停池的交易日）
    async getLimitUpStocks() {
      if (!this.formData.date) {
        this.limitUpStocks = []
        return
      }
      try {
        const res = await stockApi.getLimitUpStocks(this.formData.date)
        const actual = res?.tradeDate ? String(res.tradeDate) : ''
        if (actual && actual !== this.formData.date) {
          this.formData.date = actual
          this.$message.info(`当日无涨停池，已切到最近交易日 ${actual}`)
        }
        this.limitUpStocks = await this.attachNodeScores(
          (res?.data || []).map((stock) => this.attachLogicMatch(stock))
        )
        if (!this.limitUpStocks.length) {
          this.$message.warning('该日无涨停数据')
        }
      } catch (error) {
        console.error('获取涨停股票失败:', error)
        this.limitUpStocks = []
        this.$message.error(error?.message || '获取涨停股票失败')
      }
    },
    async getTopStocks() {
      try {
        const response = await stockApi.searchTopStocks(this.formData.date);
        const data = response.data || []
        if (response.success) {
          this.topStockOptions = data.map(stock => ({
            value: stock.code,
            label: `${stock.name} (${Number(stock.gainPercent || 0).toFixed(1)}%)`,
            concepts: stock.concepts || [],
            kplConcepts: stock.kplConcepts || [],
            localConcepts: stock.localConcepts || []
          }));
        } else {
          this.topStockOptions = [];
        }
      } catch (error) {
        this.$message.error('搜索失败');
      }
    },
    // 搜索股票
    async searchStock(query) {
      if (!query.trim()) {
        this.stockOptions = [];
        return;
      }
      
      this.searchLoading = true;
      try {
        const response = await stockApi.searchStocks(query);
        
        if (response.success && response.data) {
          this.stockOptions = response.data.map(stock => ({
            value: stock.code,
            label: `${stock.name} (${stock.code})`,
            concepts: stock.concepts || [],
            kplConcepts: stock.kplConcepts || [],
            localConcepts: stock.localConcepts || []
          }));
        } else {
          this.stockOptions = [];
        }
      } catch (error) {
        this.$message.error('搜索失败');
      } finally {
        this.searchLoading = false;
      }
    },
    
    // 选择股票
    async handleStockSelect() {
      if (this._suppressLeaderChange) return
      await this.applyLeaderConcepts({ silent: false })
      if (this.embedResults && (this.limitUpStocks || []).length) {
        await this.analyzeGapUp()
      }
    },
    async applyLeaderConcepts({ silent } = {}) {
      this.allSelectedStocks = [...this.selectedStocks,...this.selectedTopStocks]
      try {
        // 清空现有概念数据
        this.conceptsList = [];
        this.highlightedConcepts = [];
        let conceptMap = {};
        // 统计所有股票的概念
        this.allSelectedStocks.forEach(selectedStock => {
            const concepts = resolveAnalysisConcepts(
              selectedStock.concepts,
              selectedStock.kplConcepts,
              this.conceptSource,
              selectedStock.localConcepts
            )
            concepts.forEach(concept => {
              if (!conceptMap[concept]) {
                conceptMap[concept] = {
                  name: concept,
                  count: 1
                }
              } else {
                // 已有概念，增加计数
                conceptMap[concept].count++;
              }
            });
        });
        let conceptsList = Array.from(Object.values(conceptMap));
        conceptsList.sort((a, b) => {
          // 按计数降序排序，计数相同按名称升序
          if (b.count !== a.count) {
            return b.count - a.count;
          }
          return a.name.localeCompare(b.name);
        })
        this.conceptsList = conceptsList
        const totalConcepts = this.conceptsList.length;
        if (!silent) {
          this.$message.success(`已加载 ${this.allSelectedStocks.length} 只股票，共 ${totalConcepts} 个概念`);
        }
      } catch (error) {
        console.error('获取概念失败:', error);
        this.$message.error('获取概念失败');
      }
    },
    async findLeaderOption(leader) {
      const code = this.normCode6(leader && (leader.code || leader.leaderId))
      const name = String((leader && leader.name) || '').trim()
      if (!code || code === '000000') return null
      const pick = (list) => (list || []).find((item) => this.normCode6(item.value) === code) || null
      let hit = pick(this.stockOptions) || pick(this.selectedStocks)
      if (hit) return hit
      const queries = []
      if (name) queries.push(name)
      queries.push(code)
      for (const query of queries) {
        await this.searchStock(query)
        hit = pick(this.stockOptions)
        if (hit) return hit
      }
      return null
    },
    isEmbedDragonOn(card) {
      return (this.embedDragonSelected || []).indexOf(card && card.key) >= 0
    },
    selectedEmbedDragonTargets() {
      const cards = this.embedDragonCards || []
      const picked = new Set(this.embedDragonSelected || [])
      const rows = cards.filter((card) => card && picked.has(card.key))
      if (rows.length) return rows
      if (this.embedLeader && (this.embedLeader.code || this.embedLeader.leaderId)) return [this.embedLeader]
      return []
    },
    async resolveEmbedDragonStocks(targets) {
      const hits = []
      const seen = new Set()
      const list = targets || []
      for (let i = 0; i < list.length; i += 1) {
        const hit = await this.findLeaderOption(list[i])
        const code = this.normCode6(hit && hit.value)
        if (!hit || !code || seen.has(code)) continue
        seen.add(code)
        hits.push(hit)
      }
      return hits
    },
    async applyResolvedLeaders(hits) {
      const list = hits || []
      list.forEach((hit) => {
        if (!this.stockOptions.some((item) => item.value === hit.value)) {
          this.stockOptions = [hit, ...this.stockOptions]
        }
      })
      this._suppressLeaderChange = true
      this.selectedStocks = list
      try {
        await this.applyLeaderConcepts({ silent: true })
      } finally {
        this.$nextTick(() => {
          this._suppressLeaderChange = false
        })
      }
    },
    async analyzeSelectedDragons() {
      const targets = (this.embedDragonCards || []).filter((card) => this.isEmbedDragonOn(card))
      if (!targets.length) {
        this.$message.warning('请先点选龙头')
        return
      }
      const hits = await this.resolveEmbedDragonStocks(targets)
      if (!hits.length) {
        this.$message.warning('没有找到点选龙头的资料')
        return
      }
      await this.applyResolvedLeaders(hits)
      if ((this.limitUpStocks || []).length) await this.analyzeGapUp()
    },
    async syncEmbedLeader() {
      if (!this.embedResults) return
      const seq = (this._embedLeaderSeq || 0) + 1
      this._embedLeaderSeq = seq
      const hits = await this.resolveEmbedDragonStocks(this.selectedEmbedDragonTargets())
      if (!hits.length || seq !== this._embedLeaderSeq) return
      const selectedCode = (this.selectedStocks || []).map((item) => this.normCode6(item.value)).join(',')
      const nextCode = hits.map((hit) => this.normCode6(hit.value)).join(',')
      if (selectedCode !== nextCode) await this.applyResolvedLeaders(hits)
      if (seq !== this._embedLeaderSeq) return
      if ((this.limitUpStocks || []).length) await this.analyzeGapUp()
    },

    // 添加自定义概念
    async searchConcept(query) {
      if (!String(query || '').trim()) {
        this.conceptOptions = []
        return
      }
      this.conceptSearchLoading = true
      try {
        const response = await stockApi.searchConcepts(query)
        if (response.success && Array.isArray(response.data)) {
          this.conceptOptions = response.data.map((name) => ({
            value: name,
            label: name
          }))
        } else {
          this.conceptOptions = []
        }
      } catch (_error) {
        this.conceptOptions = []
      } finally {
        this.conceptSearchLoading = false
      }
    },

    addCustomConcept() {
      const concept = String(this.newConcept || '').trim()
      if (!concept) return
      const existsInCustom = this.customConcepts.some(item => item.name === concept)
      const existsInList = this.conceptsList.some(item => item.name === concept)
      if (!existsInCustom && !existsInList) {
        this.customConcepts.push({
          name: concept,
          count: 1,
          isCustom: true
        })
      }
      this.newConcept = ''
      this.conceptOptions = []
    },
    
    // 切换高亮概念
    toggleHighlight(concept) {
      const index = this.highlightedConcepts.indexOf(concept);
      if (index > -1) {
        this.highlightedConcepts.splice(index, 1);
      } else {
        this.highlightedConcepts.push(concept);
      }
    },
    
    // 分析补涨
    async analyzeGapUp() {
      if (this.embedResults) {
        const next = []
        const seen = new Set()
        for (const raw of this.embedHighlightConcepts || []) {
          const name = String(raw || '').trim()
          if (!name || seen.has(name)) continue
          seen.add(name)
          next.push(name)
        }
        this.highlightedConcepts = next
      }
      let limitUpStocks = JSON.parse(JSON.stringify(this.limitUpStocks))
      // 获取已选中股票的代码集合
      const selectedStockCodes = new Set(
        this.allSelectedStocks.map(stock => stock.value)
      )
      // 过滤掉已选中的股票
      limitUpStocks = limitUpStocks.filter(stock => 
        !selectedStockCodes.has(stock.ts_code)
      )
      limitUpStocks.forEach(stock => {
        const nameMatch = {
          score: 0,
          matchedChars: []
        }
        const conceptMatch = {
          score: 0,
          highlightedScore: 0,
          repeatConceptScore: 0,
          matchedConcepts: [],
        }
        const concepts = resolveAnalysisConcepts(stock.concepts, stock.kplConcepts, this.conceptSource, stock.localConcepts)
        // 多选大哥股票按叠加逻辑：高亮单独+5分，叠加几个加几个
        if (this.allSelectedStocks.length > 1) {
          const repeatConcepts = this.allConceptsList.filter(item => item.count > 1)
          concepts.forEach(concept => {
            if (this.highlightedConcepts.includes(concept)) {
              conceptMatch.highlightedScore+=5
              conceptMatch.matchedConcepts.push({
                name: `${concept}(5分)`,
                type: 'highlight',
                score: 5
              })
            }
            const findItem = repeatConcepts.find(item => item.name === concept)
            if (findItem) {
              conceptMatch.repeatConceptScore+=findItem.count
              conceptMatch.matchedConcepts.push({
                name: `${concept}(${findItem.count}分)`,
                type: 'repeat',
                score: findItem.count > 2 ? findItem.count : 3 // 用于排序放最前面
              })
            }
          })
        } else {
          this.selectedStocks.forEach(selectStock => {
            const selectStockName = selectStock.label.replace(/\s+/g, ''); // 去掉所有空格
            const stockName = stock.name.replace(/\s+/g, ''); // 去掉所有空格
            for (let i = 0; i < stockName.length; i++) {
              const char = stockName[i];
              if (selectStockName.includes(char)) {
                  nameMatch.score++;
                  if (!nameMatch.matchedChars.includes(char)) {
                      nameMatch.matchedChars.push(char);
                  }
              }
            }
          })
          concepts.forEach(concept => {
            if (this.highlightedConcepts.includes(concept)) {
              conceptMatch.highlightedScore+=5
              conceptMatch.matchedConcepts.push({
                name: `${concept}(5分)`,
                type: 'highlight',
                score: 5
              })
            } else {
              const findItem = this.allConceptsList.find(item => item.name === concept)
              if (findItem) {
                conceptMatch.score+=1
                conceptMatch.matchedConcepts.push({
                  name: concept,
                  type: 'general',
                  score: 1
                })
              }
            }
          })
        }

        const totalScore =
          nameMatch.score +
          conceptMatch.score +
          conceptMatch.highlightedScore +
          conceptMatch.repeatConceptScore

        if (totalScore >= 6) {
          stock.level = '高'
        } else if (totalScore >= 3) {
          stock.level = '中'
        } else {
          stock.level = '低'
        }
        conceptMatch.matchedConcepts.sort((a,b) => b.score - a.score)
        stock.nameDisplay = this.highlightMatchedText(stock.name, nameMatch.matchedChars, 'highlight-name')
        stock.nameMatchScore = nameMatch.score
        stock.conceptMatchScore = conceptMatch.score
        stock.highlightedConceptScore = conceptMatch.highlightedScore
        stock.repeatConceptScore = conceptMatch.repeatConceptScore,
        stock.totalScore = totalScore.toFixed(2)
        stock.matchedConcepts = conceptMatch.matchedConcepts
        stock.logicMatch = this.logicMatchByCode[stock.ts_code] || null
      })
      limitUpStocks.sort((a, b) => b.totalScore - a.totalScore)
      this.analyzeLimitUpStocks = limitUpStocks
      // 设置已查询标记
      this.hasAnalyzed = true;
      
      this.emitAutoRelay(limitUpStocks)
      // 查询后清除排序条件，按总分降序
      this.sortProp = 'totalScore';
      this.sortOrder = 'descending';
      // 清除表格的排序状态
      this.$nextTick(() => {
        const table = this.$refs.limitUpTable;
        if (table && table.clearSort) {
          table.clearSort();
        }
      });
    },
    // 高亮显示匹配的文本
    highlightMatchedText(text, matchedChars, className) {
        if (!text || !matchedChars || matchedChars.length === 0) return text;
        
        let result = String(text);
        matchedChars.forEach(char => {
            const regex = new RegExp(`(${char})`, 'g');
            result = result.replace(regex, `<span class="${className}">$1</span>`);
        });
        return result;
    },
    // 获取命中概念标签类型
    getMatchedTagType(type) {
      switch (type) {
        case 'general': return '';
        case 'highlight': return 'warning';
        case 'repeat': return 'danger';
        default: return '';
      }
    },
    // 获取等级标签类型
    getLevelTagType(level) {
      switch (level) {
        case '高': return 'danger';
        case '中': return 'warning';
        case '低': return 'info';
        default: return '';
      }
    },
    async loadDayNodeScore() {
      const td = this.formData.date;
      this.dayNodeScore = null;
      this.dayNodeHit = false;
      this.dayNodeHits = [];
      this.dayNodeDailyDoc = null;
      this.dayNodeFactorSource = '';
      if (!td) return;
      try {
        const doc = await stockApi.fetchDailyData(td);
        if (!doc) return;
        this.dayNodeDailyDoc = doc;
        this.dayNodeFactorSource = doc.nodeFactorSource || '';
        if (doc.greedyNodeScore != null && doc.greedyNodeScore !== '') {
          this.dayNodeScore = Number(doc.greedyNodeScore);
          this.dayNodeHit = !!doc.greedyNodeHit;
          this.dayNodeHits = Array.isArray(doc.greedyNodeHits)
            ? doc.greedyNodeHits.map(String)
            : [];
        }
      } catch (e) {
        /* 无数据保持待更新 */
      }
    },
    formatDayNodeSnapshotValue(field, raw) {
      const n = Number(raw);
      if (!Number.isFinite(n)) return String(raw);
      if (field.kind === 'int') return String(Math.round(n));
      if (field.kind === 'pct') {
        const digits = Math.abs(n) >= 10 ? 1 : 2;
        return `${n.toFixed(digits)}%`;
      }
      return String(Math.round(n * 100) / 100);
    },
    async loadGreedyNodeCatalog() {
      try {
        const res = await stockApi.fetchGreedyNodeCatalog();
        if (res?.ok !== false) {
          this.greedyNodeScheme = res.scheme || 'V5F';
          this.greedyNodeThreshold = res.threshold ?? 7;
          this.greedyNodeCatalog = Array.isArray(res.nodes) ? res.nodes : [];
        }
      } catch (e) {
        /* 保持默认 V5F */
      }
    },
    getGreedyNodeMeta(id) {
      return this.greedyNodeCatalogMap[id] || null;
    },
    getGreedyNodeWeight(id) {
      const meta = this.getGreedyNodeMeta(id);
      return meta?.weight ?? '?';
    },
    getGreedyNodeFactor(id) {
      return this.getGreedyNodeMeta(id)?.factor || '—';
    },
    getGreedyNodeRule(id) {
      return this.getGreedyNodeMeta(id)?.rule || '暂无规则说明';
    },
    async changeDate() {
      if (this.lccBoard) this.loadRelayExclude()
      await this.getLimitUpStocks()
      this.loadLogicMatchStorage()
      this.loadTopLibFlags()
      this.getTopStocks()
      this.loadDayNodeScore()
      this.loadDaySoil()
      if (this.embedResults) await this.syncEmbedLeader()
    },
    normCode6(code) {
      return String(code || '')
        .trim()
        .split('.')[0]
        .replace(/\D/g, '')
        .padStart(6, '0')
        .slice(-6)
    },
    async loadTopLibFlags() {
      const date = this.formData.date
      this.topLibFlagsByCode = {}
      if (!date) return
      try {
        const res = await stockApi.fetchAuctionTopLibrary(date)
        const rows = Array.isArray(res?.rows) ? res.rows : []
        const map = {}
        for (const r of rows) {
          const c = this.normCode6(r && r.code)
          if (!c || c === '000000') continue
          const tag = String((r && r.tag) || '')
            .trim()
            .toUpperCase()
          let flags = 0
          if (tag === 'T0') flags = 2
          else if (tag === 'T1') flags = 1
          else if (tag === '') flags = 0
          // 同码多条取更大旗数
          if (flags > (map[c] || 0)) map[c] = flags
        }
        this.topLibFlagsByCode = map
      } catch (_e) {
        this.topLibFlagsByCode = {}
      }
    },
    topFlagsForRow(row) {
      const c = this.normCode6(row && (row.ts_code || row.code))
      if (!c || c === '000000') return 0
      return Number(this.topLibFlagsByCode[c]) || 0
    },
    topFlagsTextForRow(row) {
      const n = this.topFlagsForRow(row)
      if (n <= 0) return ''
      return '🚩'.repeat(Math.min(n, 2))
    },
    async loadLogicMatchStorage() {
      const date = this.formData.date
      if (!date) {
        this.logicMatchByCode = {}
        return
      }
      const local = loadGapUpLogicByCode(date)
      try {
        const res = await stockApi.fetchGapUpLogicMatch(date)
        const remote = res?.ok ? (res.logicMatchByCode || {}) : {}
        const merged = { ...remote }
        for (const [code, entry] of Object.entries(local)) {
          if (!remote[code]) {
            merged[code] = entry
            stockApi.saveGapUpLogicMatch(date, code, entry).catch(() => {})
          }
        }
        this.logicMatchByCode = merged
        saveGapUpLogicMap(date, merged)
      } catch (_e) {
        this.logicMatchByCode = local
      }
      this.syncAllLogicToLists()
    },
    async persistLogicMatch(code, entry) {
      const date = this.formData.date
      if (!date || !code) return
      this.logicMatchByCode = saveGapUpLogicEntry(date, code, entry)
      this.syncLogicToLists(code)
      try {
        const res = await stockApi.saveGapUpLogicMatch(date, code, entry)
        if (res?.logicMatchByCode) {
          this.logicMatchByCode = res.logicMatchByCode
          saveGapUpLogicMap(date, res.logicMatchByCode)
          this.syncAllLogicToLists()
        }
      } catch (_e) {
        /* sessionStorage 已缓存，下次加载会重试同步 */
      }
    },
    attachLogicMatch(stock) {
      return {
        ...stock,
        logicMatch: this.logicMatchByCode[stock.ts_code] || null
      }
    },
    syncLogicToLists(code) {
      const logicMatch = this.logicMatchByCode[code] || null
      const apply = (stock) => {
        if (stock.ts_code === code) stock.logicMatch = logicMatch
      }
      this.limitUpStocks.forEach(apply)
      this.analyzeLimitUpStocks.forEach(apply)
    },
    syncAllLogicToLists() {
      const apply = (stock) => {
        stock.logicMatch = this.logicMatchByCode[stock.ts_code] || null
      }
      this.limitUpStocks.forEach(apply)
      this.analyzeLimitUpStocks.forEach(apply)
    },
    getRowLogicMatch(row) {
      return row?.logicMatch || this.logicMatchByCode[row?.ts_code] || null
    },
    hasLogicMatch(row) {
      return hasGapUpLogicMatch(this.getRowLogicMatch(row))
    },
    formatLogicDisplay(row) {
      return formatGapUpLogicDisplay(this.getRowLogicMatch(row))
    },
    goToConceptMatch(row) {
      const boardsRaw = Number(row.consecutiveBoards)
      const query = {
        gapUp: '1',
        sourceCode: row.ts_code,
        sourceName: row.name,
        date: this.formData.date,
        conceptSource: String(this.conceptSource)
      }
      if (Number.isFinite(boardsRaw) && boardsRaw >= 1) {
        query.boards = String(Math.floor(boardsRaw))
      }
      this.$router.push({
        path: '/concept-match',
        query
      })
    },
    setLogicSwitch(row) {
      const code = row.ts_code
      this.persistLogicMatch(code, { type: 'switch' })
      this.$message.success('已标记切换')
    },
    checkboxChange() {
      this.onlyMainBoard
      this.filterMarketValue
    },
    onOnlyMainBoardChange(checked) {
      if (checked) this.onlyChiNext = false
    },
    onOnlyChiNextChange(checked) {
      if (checked) this.onlyMainBoard = false
    },
    // 排序方法
    sortChange({ prop, order }) {
      // 如果什么都没选（order为null），根据是否查询过使用不同的默认排序
      if (order === null) {
        if (this.hasAnalyzed) {
          // 查询后默认按总分降序
          this.sortProp = 'totalScore';
          this.sortOrder = 'descending';
        } else {
          // 查询前默认按连板高度降序
          this.sortProp = 'consecutiveBoards';
          this.sortOrder = 'descending';
        }
      } else {
        // 点一个后默认其他清除
        this.sortProp = prop;
        this.sortOrder = order;
      }
      // 需要强制更新视图
      this.$forceUpdate();
    },
    // 执行排序的方法
    sortTableData(data) {
      // 创建数组副本避免修改原数组
      const sortedData = [...data];
      
      // 如果没有排序属性或顺序，使用默认排序
      if (!this.sortProp || !this.sortOrder) {
        return this.getDefaultSortedData(sortedData);
      }
      
      return sortedData.sort((a, b) => {
        let aValue = a[this.sortProp];
        let bValue = b[this.sortProp];
        
        // 处理可能为null或undefined的值
        if (aValue == null) return this.sortOrder === 'descending' ? 1 : -1;
        if (bValue == null) return this.sortOrder === 'descending' ? -1 : 1;
        
        let comparison = 0;

        comparison = Number(aValue) - Number(bValue);

        // 根据排序顺序返回结果
        return this.sortOrder === 'descending' ? -comparison : comparison;
      });
    },

    // 获取默认排序的数据
    getDefaultSortedData(data) {
      const sortedData = [...data];
      
      if (this.hasAnalyzed) {
        // 查询后：按总分降序
        return sortedData.sort((a, b) => b.totalScore - a.totalScore);
      } else {
        // 查询前：按连板高度降序
        return sortedData.sort((a, b) => b.consecutiveBoards - a.consecutiveBoards);
      }
    },

    // 将时间字符串转换为秒数
    timeToSeconds(timeStr) {
      if (!timeStr) return 0;
      
      try {
        const parts = timeStr.split(':');
        if (parts.length === 3) {
          const hours = parseInt(parts[0]) || 0;
          const minutes = parseInt(parts[1]) || 0;
          const seconds = parseInt(parts[2]) || 0;
          return hours * 3600 + minutes * 60 + seconds;
        }
        return 0;
      } catch (e) {
        console.error('时间格式解析错误:', timeStr, e);
        return 0;
      }
    },
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize);
    this.loadGreedyNodeCatalog()
    this.changeDate()
  },
  activated() {
    this.loadLogicMatchStorage()
    this.loadTopLibFlags()
    this.syncAllLogicToLists()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  }
};
</script>

<style lang="scss" scoped>
.gap-up-analysis {
  height: 100%;
  display: flex;
  flex-direction: column;
  &.is-embed-results {
    height: 720px;
    min-height: 560px;
    background: #fff;
    .results-area {
      padding: 4px 4px 8px;
      border-radius: 0;
    }
    .embed-leader-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      .search-area-label {
        color: #606266;
        font-size: 13px;
        flex-shrink: 0;
      }
      .embed-leader-select {
        flex: 1;
        min-width: 220px;
        max-width: 480px;
      }
      .dragon-card {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
        border: 1px solid #d5e4f7;
        background: #f7faff;
        border-radius: 8px;
        padding: 2px 8px 3px;
        cursor: pointer;
        line-height: 1.15;
      }
      .dragon-card em {
        font-style: normal;
        font-size: 10px;
        color: #7a8eab;
      }
      .dragon-card b {
        font-size: 12px;
        font-weight: 700;
        color: #243044;
      }
      .dragon-card.on.tone-leader {
        border-color: #e0b15a;
        background: #fff6e4;
        box-shadow: 0 0 0 1px #e0b15a;
      }
      .dragon-card.on.tone-trend {
        border-color: #1a73e8;
        background: #e8f1ff;
        box-shadow: 0 0 0 1px #1a73e8;
      }
      .dragon-card.on.tone-board {
        border-color: #e07aa2;
        background: #fff1f6;
        box-shadow: 0 0 0 1px #e07aa2;
      }
    }
    ::v-deep .el-table {
      font-size: 12px;
      th.el-table__cell {
        padding-top: 4px;
        padding-bottom: 4px;
      }
      th .cell,
      td .cell {
        padding-left: 4px;
        padding-right: 4px;
        text-align: center;
      }
      th .cell {
        position: relative;
        white-space: nowrap;
        word-break: keep-all;
        line-height: 1.2;
        text-overflow: clip;
        overflow: visible;
      }
      th.is-sortable .cell {
        padding-right: 16px;
      }
      td .cell {
        white-space: nowrap;
      }
      td.gap-up-concepts-col .cell {
        white-space: normal;
        word-break: break-all;
      }
      .caret-wrapper {
        position: absolute;
        right: 0;
        top: 50%;
        width: 14px;
        height: 18px;
        transform: translateY(-50%);
      }
      tr.is-embed-picked > td {
        background: #e8f1ff !important;
      }
      .logic-cell {
        justify-content: center;
        align-items: center;
      }
      .logic-cell__text {
        flex: 1;
        min-width: 0;
        text-align: center;
      }
      .gap-up-concept-tags {
        justify-content: center;
      }
      .logic-cell .el-button--mini {
        padding: 4px 6px;
      }
      .chip-review-cell--splus {
        padding: 1px 4px;
      }
      .chip-review-cell--splus .chip-review-cell__hint {
        display: none;
      }
    }
  }
  .search-area {
    background: #fff;
    padding: 20px;
    border-radius: 4px;
    margin-bottom: 8px;
    position: relative;
    z-index: 2;
    overflow: visible;

    .search-head {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 12px;
    }
    .toolbar-line {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }
    .toolbar-line--date {
      flex: 0 0 auto;
    }
    .toolbar-line--actions {
      .toolbar-btn {
        margin-left: 0;
        flex-shrink: 0;
      }
    }
    .search-area-label {
      margin-right: 6px;
      color: #606266;
    }
    .search-area-select--date {
      width: 150px;
    }
    .leader-stack {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex: 1 1 auto;
      flex-wrap: nowrap;
      gap: 12px;
      min-width: 0;
      .search-area-label {
        flex: 0 0 auto;
        margin-right: 6px;
        color: #606266;
      }
      .leader-col__row {
        display: flex;
        align-items: center;
        flex: 1 1 0;
        min-width: 0;
        width: auto;
        gap: 4px 8px;
      }
      .leader-col__row--node {
        flex: 1 1 0;
        ::v-deep .el-popover__reference-wrapper,
        ::v-deep > span {
          display: block;
          width: 100%;
        }
      }
      .search-area-select--leader {
        flex: 1;
        min-width: 0;
        width: auto;
      }
      .day-node-panel {
        width: 100%;
        min-height: 40px;
        white-space: nowrap;
        box-sizing: border-box;
      }
    }
    .analyze-btn {
      margin-left: 10px;
    }
    .day-node-panel {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px 10px;
      width: 100%;
      padding: 10px 12px;
      border-radius: 6px;
      border: 1px solid #dcdfe6;
      background: linear-gradient(135deg, #f5f7fa 0%, #fff 100%);
      cursor: pointer;
      user-select: none;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:hover {
        border-color: #c0c4cc;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      }

      &--hit {
        border-color: #f3d19e;
        background: linear-gradient(135deg, #fdf6ec 0%, #fff9f0 100%);
        .day-node-panel__score {
          color: #e6a23c;
        }
      }

      &--pending {
        opacity: 0.85;
      }

      &__label {
        font-size: 13px;
        font-weight: 600;
        color: #303133;
      }

      &__scheme {
        font-size: 11px;
        font-weight: 600;
        color: #409eff;
        background: #ecf5ff;
        border: 1px solid #b3d8ff;
        border-radius: 3px;
        padding: 0 5px;
        line-height: 18px;
      }

      &__score {
        font-size: 22px;
        font-weight: 700;
        color: #303133;
        line-height: 1;
      }

      &__mark {
        color: #e6a23c;
        font-size: 16px;
      }

      &__status {
        font-size: 12px;
        font-weight: 600;
        color: #e6a23c;
        background: rgba(230, 162, 60, 0.12);
        border-radius: 3px;
        padding: 1px 6px;
      }

      &__pending {
        font-size: 14px;
        color: #909399;
      }

      &__arrow {
        margin-left: auto;
        color: #909399;
        font-size: 12px;
      }
    }
  }

  /* Web：工具栏行横向紧凑（龙头/TOP 单独一行 50% 并排） */
  @media screen and (min-width: 1024px) {
    .search-area {
      .search-row--toolbar.el-row {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 8px;
      }

      .search-row--toolbar.el-row > .el-col {
        flex: 0 0 auto !important;
        width: auto !important;
        max-width: none !important;
      }

      .search-row .search-area-label {
        margin-right: 6px;
      }

      .analyze-btn {
        margin-left: 0;
      }

      .analyze-btn + .analyze-btn {
        margin-left: 8px;
      }
    }
  }

  .concepts-area {
    background: #fff;
    padding: 8px 20px;
    border-radius: 4px;
    margin-top: 0;
    margin-bottom: 10px;
    position: relative;
    z-index: 1;
    clear: both;
    &.is-collapsed {
      padding-top: 6px;
      padding-bottom: 6px;
    }
    .concepts-title {
      font-weight: bold;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      user-select: none;
      min-height: 28px;
      .concepts-toggle {
        border: none;
        background: transparent;
        padding: 0 2px;
        margin: 0;
        cursor: pointer;
        color: #606266;
        font-size: 14px;
        line-height: 1;
        flex-shrink: 0;
      }
      .concepts-title__text {
        flex-shrink: 0;
      }
      .add-concept {
        display: flex;
        align-items: center;
        margin-left: 10px;
        .concepts-title-input {
          width: 200px;
          margin-right: 10px;
        }
      }
    }
    .concepts-list {
      display: flex;
      flex-wrap: wrap;
      min-height: 0;
      max-height: 200px;
      overflow-y: auto;
      gap: 15px;
      padding-top: 10px;
      margin-top: 4px;
      .concept-tag {
        cursor: pointer;
        transition: all 0.3s;
      }
      .concept-tag:hover {
        transform: translateY(-2px);
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      }
      @media screen and (min-width: 769px) {
        ::v-deep {
          .el-badge__content.is-fixed {
            top: 5px;
            right: 15px;
          }
        }
      }
    }
  }
  .results-area {
    background: #fff;
    padding: 10px 20px 20px 20px;
    border-radius: 4px;
    flex-grow: 1;
    height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
    .results-area-filter {
      margin-bottom: 10px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 12px;
      .filter-rel-vol-label {
        margin-left: 4px;
        color: #606266;
        font-size: 14px;
        flex-shrink: 0;
      }
      .chip-tier-filter-row {
        display: inline-flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 8px;
        flex-basis: 100%;
        max-width: 100%;
      }
      .lcc-sync-stack {
        margin-left: auto;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 6px;
        flex-shrink: 0;
      }
      .lcc-sync-stack .sync-ths-btn {
        margin-left: 0;
      }
      .sync-ths-btn {
        margin-left: auto;
        flex-shrink: 0;
        padding: 5px 10px;
        font-size: 12px;
      }
      .relay-add-btn {
        margin-left: auto;
        height: 24px;
        padding: 0 12px;
        border: 1px solid #1a73e8;
        border-radius: 6px;
        background: #1a73e8;
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
      }
      .relay-add-btn.on {
        background: #fff;
        color: #1a73e8;
      }
      .chip-tier-buttons {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 4px;
      }
      .chip-tier-btn {
        border: 1px solid #dcdfe6;
        background: #fff;
        color: #606266;
        font-size: 12px;
        line-height: 1.2;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover {
          color: #409eff;
          border-color: #c6e2ff;
        }

        &.is-active {
          color: #409eff;
          border-color: #409eff;
          background: #ecf5ff;
        }

        &.is-splus.is-active {
          color: #8a6a2f;
          border-color: #c4a35a;
          background: #faf6ee;
        }
      }
      .filter-current-count {
        margin-left: 6px;
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
      }
      .filter-rel-vol-group {
        display: inline-flex;
        flex-wrap: wrap;
        align-items: center;
      }
    }
    .table-wrap {
      flex: 1;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
    }
    ::v-deep {
      .el-table {
        flex-grow: 1;
        td.gap-cell-rank-1 .cell {
          font-weight: 700;
          color: #e91e8c;
        }
        td.gap-cell-rank-2 .cell {
          font-weight: 700;
          color: #de2910;
        }
        /* Web：概念标签可多行；H5 见下方 @media，单行横向拖动 */
        @media screen and (min-width: 769px) {
          td.gap-up-concepts-col .cell {
            white-space: normal !important;
            word-break: break-word;
          }
        }
        /* 逻辑列：覆盖 App.vue 表格全局 nowrap，允许自动换行 */
        td.gap-up-logic-col .cell {
          white-space: normal !important;
          word-break: break-word;
          line-height: 1.35;
        }
        .highlight-name {
          background-color: #fffacd;
          color: #d48806;
          padding: 2px;
          border-radius: 3px;
          font-weight: 500;
        }
        .gap-up-name-cell {
          display: inline-flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 2px;
          line-height: 1.25;
        }
        .gap-up-top-flags {
          font-size: 12px;
          letter-spacing: 0;
          line-height: 1;
          white-space: nowrap;
        }
        .chip-review-cell {
          display: inline-flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 4px 6px;
          line-height: 1.25;
          max-width: 100%;
        }
        .chip-review-cell__score {
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          color: #303133;
        }
        .chip-review-cell__tier {
          font-size: 12px;
          color: #606266;
        }
        .chip-review-cell--s .chip-review-cell__score {
          color: #5c4b2a;
        }
        .chip-review-cell--a .chip-review-cell__score {
          color: #3a4a5c;
        }
        .chip-review-cell--b .chip-review-cell__score,
        .chip-review-cell--b .chip-review-cell__tier {
          color: #606266;
          font-weight: 500;
        }
        .chip-review-cell--splus {
          padding: 3px 8px;
          border-radius: 6px;
          border: 1px solid rgba(196, 163, 90, 0.55);
          background: linear-gradient(180deg, #fbf7ef 0%, #f3ead8 100%);
          box-shadow: 0 1px 2px rgba(138, 106, 47, 0.08);
        }
        .chip-review-cell--splus .chip-review-cell__mark {
          color: #b08d3a;
          font-size: 12px;
          line-height: 1;
        }
        .chip-review-cell--splus .chip-review-cell__score {
          font-weight: 800;
          color: #6e5420;
          letter-spacing: 0.02em;
        }
        .chip-review-cell--splus .chip-review-cell__badge {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
          padding: 2px 6px;
          border-radius: 999px;
          color: #6e5420;
          background: rgba(196, 163, 90, 0.22);
          border: 1px solid rgba(176, 141, 58, 0.35);
        }
        .chip-review-cell--splus .chip-review-cell__hint {
          flex-basis: 100%;
          font-size: 10px;
          color: #9a7d45;
          letter-spacing: 0.04em;
        }
      }
    }

    .gap-up-concept-tags {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
    }
  }

  /*
   * H5：不再拉宽整张表（避免表头/表体错位、概念溢出表格外）。
   * 概念在「单元格内」单行横滑，表头与列宽与 Element 默认一致。
   */
  @media screen and (max-width: 768px) {
    .results-area {
      ::v-deep td.gap-up-concepts-col .cell {
        overflow: hidden !important;
        vertical-align: middle;
        /* 覆盖 App.vue 表格全局 nowrap，否则单元格裁切异常 */
        white-space: normal !important;
      }

      .gap-up-concept-tags--h5-strip {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }

        ::v-deep .el-tag {
          flex-shrink: 0;
        }
      }
    }
  }

  @media screen and (max-width: 1023px) {
    .search-area {
      padding: 10px;
      margin-bottom: 10px;
      .search-row {
        .search-area-label {
          display: inline-block;
          margin-bottom: 5px;
        }
        .search-area-select {
          width: calc(100% - 60px);
          max-width: 200px;
        }
      }
    }
    .concepts-area {
      padding: 10px;
      margin-bottom: 10px;
      .concepts-title {
        margin: 0 0 5px 0;
        .add-concept {
          margin-left: 0;
          .concepts-title-input {
            width: 180px;
          }
        }
      }
      .concepts-list {
        max-height: 60px;
        gap: 5px;
      }
    }
    .results-area {
      padding: 10px;
    }
    .sm-mt {
      margin-top: 15px; /* 或其他合适的间距 */
    }
  }
  @media screen and (min-width: 1024px) and (max-width: 1024px) {
    .md-mt {
      margin-top: 15px; /* 或其他合适的间距 */
    }
  }

  .logic-cell {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 2px 4px;
    max-width: 100%;

    &__text {
      font-size: 12px;
      color: #303133;
      line-height: 1.35;
      white-space: normal;
      word-break: break-word;
      overflow-wrap: anywhere;
      flex: 1;
      min-width: 0;
    }

    &__switch {
      padding: 0 2px;
      font-size: 12px;
      flex-shrink: 0;
    }

    &__dot {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #409eff;
      cursor: pointer;
      transition: transform 0.15s ease, box-shadow 0.15s ease;

      &:hover {
        transform: scale(1.15);
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.25);
      }
    }

    ::v-deep .el-button--mini {
      flex-shrink: 0;
      padding: 5px 6px;
    }
  }
}

.node-star {
  margin-right: 2px;
  color: #13c2c2;
  font-weight: 700;
  text-shadow: 0 0 0.5px #08979c;
}

.soil-board-mark {
  margin-left: 3px;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.02em;
  border-radius: 4px;
  padding: 0 3px;
  line-height: 1.4;
}

.soil-board-mark.is-splus {
  color: #8b1515;
  background: linear-gradient(145deg, #fff6d8 0%, #f0d078 55%, #e8c468 100%);
  box-shadow: inset 0 0 0 1px rgba(212, 180, 90, 0.9);
}

.soil-board-mark.is-s {
  color: #b42318;
  background: linear-gradient(145deg, #fff1f0 0%, #ffd4d0 55%, #ffc2bc 100%);
  box-shadow: inset 0 0 0 1px rgba(240, 168, 160, 0.95);
}

.chip-v2-overextended {
  color: #e6a23c;
  font-weight: 600;
}

</style>

<style lang="scss">
.gap-up-node-popover {
  padding: 0 !important;

  .greedy-node-hits-panel {
    max-height: 520px;
    overflow-y: auto;
    padding: 12px 14px;

    &__head {
      font-size: 13px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #ebeef5;
    }

    &__complete {
      font-size: 12px;
      margin-bottom: 10px;
      padding: 6px 8px;
      border-radius: 4px;
      line-height: 1.5;

      &.is-ok {
        color: #67c23a;
        background: rgba(103, 194, 58, 0.08);
      }

      &.is-warn {
        color: #e6a23c;
        background: rgba(230, 162, 60, 0.08);
      }
    }

    &__source {
      margin-left: 6px;
      padding: 0 5px;
      border-radius: 3px;
      font-size: 11px;
      color: #909399;
      background: #f4f4f5;
    }

    &__snapshot {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px 12px;
      margin-bottom: 12px;
      padding-bottom: 10px;
      border-bottom: 1px dashed #ebeef5;
    }

    &__snap-item {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 8px;
      font-size: 12px;
      line-height: 1.45;

      &.is-missing .greedy-node-hits-panel__snap-label,
      &.is-missing .greedy-node-hits-panel__snap-value {
        color: #f56c6c;
      }
    }

    &__snap-label {
      color: #909399;
      flex-shrink: 0;
    }

    &__snap-value {
      color: #303133;
      font-weight: 500;
      text-align: right;
      word-break: break-all;
    }

    &__section-title {
      font-size: 12px;
      font-weight: 600;
      color: #606266;
      margin-bottom: 8px;
    }

    &__score {
      font-size: 13px;
      color: #606266;
      margin-bottom: 8px;

      b {
        font-size: 18px;
        color: #e6a23c;
        margin: 0 4px;
      }
    }

    &__badge {
      font-size: 12px;
      color: #e6a23c;
      background: rgba(230, 162, 60, 0.12);
      border-radius: 3px;
      padding: 1px 6px;
      margin-left: 4px;

      &--muted {
        color: #909399;
        background: #f4f4f5;
      }
    }

    &__empty {
      font-size: 13px;
      color: #909399;
      line-height: 1.5;
    }

    &__item:not(:last-child) {
      margin-bottom: 10px;
      padding-bottom: 10px;
      border-bottom: 1px dashed #ebeef5;
    }

    &__title {
      font-size: 13px;
      line-height: 1.5;
      margin-bottom: 4px;
    }

    &__weight {
      color: #e6a23c;
      font-weight: 600;
      margin-left: 4px;
    }

    &__factor {
      color: #909399;
      margin-left: 6px;
      font-size: 12px;
    }

    &__rule {
      font-size: 12px;
      color: #606266;
      line-height: 1.55;
    }
  }
}
</style>